

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import type { GroupBase } from "react-select";
import { showSuccess, showError, showInfo } from "toast/toast";
import { useNavigate, useLocation } from "react-router-dom";
import CustomFieldBuilder from "components/CustomFieldBuilder";
import DynamicForm from "components/DynamicForm";
import { MobileCardsOnly } from "components/TableandMobilecomponent";
import { CustomField, Media } from "types";
import { UIInputFF } from "components/Adapter";
import { UIReactSelect } from "components/WarehouseInputfields";
import { fetchBrandsByUserSelect2, fetchCategoriesByUserSelect2 } from "api/postTypeApiCalls";
import { Package, Settings } from 'lucide-react';
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import { useReactTable, getCoreRowModel, createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import { useBrandCategorySelector } from "components/BrandCategorySelector";
import SignageVolumeDiscount, { type VolumeDiscountUI } from "./SignageVolumeDiscount";// import SignageVolumeDiscount, {
//   VolumeDiscount,
//   VolumeDiscountCombination,
// } from "./SignageVolumeDiscount";
import DesignsTab, { type ProductCombination } from "components/DesignsTab";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";


import {
  Brand,
  Category,
  Warehouse,
  Attribute,
  TaxGroup,
  TaxRule
} from "types";
import { nameValidation, statusValidation } from "validationSchemas/common";
import { getAuthCookie } from "../../utils/authCookies";
const INPUT_BG = "#f5f6f8";
const BORDER = "#e5e7eb";
type FormValues = {
  name: string;
  product_type: string;
  sku: string;

  description: string;
  brand: string;
  category: string;
  item_condition: string;
  condition_note: string;
  purchase_price: string;
  retail_price: string;
  length: string;
  height: string;
  width: string;
  weight: string;
  threshold_qty: string;
  attribute: string;
  parameter: string;
  notes: string;
  images: File[] | null;
  statuss: string;
  is_rotational: string;
  is_production: string;
  warehouseId: string;
  on_hand: string;
  available: string;
  reserved: string;
  is_taxable: boolean;
  tax_rule_id: string;
  taxRule: TaxRule | null;
  taxGroup: TaxGroup | null;
  shipping_config: "global" | "per_variant";
};
type CombRow = {
  sku?: string;
  attributes: Record<string, string>;
  price: number;
};
type WarehouseRow = {
  warehouse_id: string;
  warehouse_name: string;
  zone_id: string;
  zone_name: string;
  rack_id: string;
  rack_name: string;
  bin_id: string;
  bin_name: string;
  on_hand: number;
  available: number;
  reserved: number;
};

export type CombinationRow = {
  id?: string
  variant_name: string;
  sku: string;
  description: string;
  attributes: Record<string, string>;
  price: number;
  length: number;
  width: number;
  height: number;
  weight: number;
  warehouseData: WarehouseRow[];
  imageIds?: string[];
  imageLocalKeys?: string[];
  variant_id?: string;
};
type VolumeDiscountCombination = {
  id: string;
  attributes: Record<string, any>;
  variant_name: string;
  sku: string;
};
type VariantApiItem = {
  variant_id?: string;
  id?: string;
  variant_name?: string;
  sku?: string;
  attributes?: Record<string, any>;
  // ... other fields from the API you don't need here
};


// ---------- Types ----------
export type VolumeDiscount = {
  min_qty: number;
  max_qty?: number;
  price?: number; // absolute price for this tier (optional)
  percent?: number; // percent off (optional)
};


const columnHelper = createColumnHelper<CombRow>();



const SignageAddVariableProduct = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const organizationId = getAuthCookie("organizationId");
  const membershipId = getAuthCookie("membershipId");
  const [preview, setPreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const safeNavigate = createSafeNavigate(navigate);

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  // ✅ Use the brand-category selector hook with userId (for both add and edit mode)
  const {
    selectedBrand,
    setSelectedBrand,
    selectedCategories,
    setSelectedCategories,
    brandOptions: brandOptionsFromHook,
    filteredCategories: categoryOptionsFromHook,
    loadingBrands,
    loadingCategories,
    brandCategoryIds,
  } = useBrandCategorySelector(userId || "", token || "");

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [options, setOptions] = useState<Record<string, string>[]>([]);
  const [taxGroups, setTaxGroups] = useState<TaxGroup[]>([]);
  const [taxRules, setTaxRules] = useState<TaxRule[]>([]);
  const [customFields, setCustomFields] = useState<CustomField[] | undefined>(undefined);
  const [combErrors, setCombErrors] = useState<{ zone: boolean; rack: boolean; bin: boolean }[][]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // IDs the user removed in UI; we will delete them on Save
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [volumeDiscounts, setVolumeDiscounts] = useState<VolumeDiscountUI[]>([]);
  const [productId, setProductId] = React.useState<string>();
  const [combinationsfetch, setCombinationsFetch] = useState<VariantApiItem[]>([]);
  const [variantsRefreshKey, setVariantsRefreshKey] = useState(0);
  const [variables, setVariables] = useState<ProductCombination[]>([]);

  // Transform variant data to ProductCombination format for DesignsTab
  const transformVariantsToCombinations = (variants: any[]): ProductCombination[] => {
    if (!Array.isArray(variants) || variants.length === 0) {
      return [];
    }
    return variants.map((variant: any, index: number) => ({
      id: variant.variant_id || variant._id || variant.id || String(index),
      sno: index + 1,
      attributes: variant.attributes || {},
      price: variant.retail_price || 0,
      variant_name: variant.variant_name || "",
    } as ProductCombination & { variant_name?: string }));
  };
  const [imagesPreparing, setImagesPreparing] = useState(false);
  const [imagesPrepared, setImagesPrepared] = useState(false);
  const [deletingIds, setDeletingIds] = React.useState<Set<string | number>>(new Set());



  // const [skuError, setSkuError] = useState(false);
  const [selectedWarehouseName, setSelectedWarehouseName] = useState<
    string | null
  >(null);

  const [locationOptions, setLocationOptions] = useState<{
    [warehouseId: string]: {
      zones: any[];
      racks: any[];
      bins: any[];
    };
  }>({});
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);




  const [warehouseData, setWarehouseData] = useState<
    {
      warehouse_id: string;
      warehouse_name: string;
      zone_id: string;
      zone_name: string;
      rack_id: string;
      rack_name: string;
      bin_id: string;
      bin_name: string;
      on_hand: number;
      available: number;
      reserved: number;
    }[]
  >([]);

  const [showWarehouseIndex, setShowWarehouseIndex] = useState<number | null>(
    null
  );

  // Function to toggle warehouses visibility for a specific row
  const toggleWarehouses = (index: number) => {
    setShowWarehouseIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  // ✅ Refs to track pending API calls and prevent duplicates
  const fetchingZonesRef = React.useRef<Set<string>>(new Set());
  const fetchingRacksRef = React.useRef<Set<string>>(new Set());
  const fetchingBinsRef = React.useRef<Set<string>>(new Set());
  /** Latest zone id we want racks for per warehouse — drop stale hierarchy responses */
  const rackFetchTargetByWarehouseRef = React.useRef<Record<string, string>>({});

  const handleEditCombination = (index: number) => {
    setIsEditMode(true);
    setEditIndex(index);
  };


  const saveEditCombination = () => {
    setIsEditMode(false);
    setEditIndex(null);
    showSuccess("Combination updated successfully!");
  };

  // Populate warehouseData when warehouses are fetched
  useEffect(() => {
    if (warehouses && warehouses.length > 0) {
      const initialData = warehouses.map((warehouse) => ({
        warehouse_id: warehouse._id,
        warehouse_name: warehouse.label,
        zone_id: "",
        zone_name: "",
        rack_id: "",
        rack_name: "",
        bin_id: "",
        bin_name: "",
        on_hand: 0,
        available: 0,
        reserved: 0,
      }));
      setWarehouseData(initialData);
    }
  }, [warehouses]);

  useEffect(() => {
    async function loadLocationOptions() {
      if (!isEditMode || editIndex === null) return;
      const opts: typeof locationOptions = {};
      for (const wh of warehouses || []) {
        opts[wh._id] = { zones: [], racks: [], bins: [] };
        try {
          const zoneData = await fetchHierarchy({ warehouse_id: wh._id });
          opts[wh._id].zones = zoneData.zones || [];

          const row = combinations[editIndex].warehouseData.find(wd => wd.warehouse_id === wh._id);
          if (row?.zone_id) {
            const rackData = await fetchHierarchy({ zone_id: row.zone_id });
            opts[wh._id].racks = rackData.racks || [];

            if (row.rack_id) {
              const binData = await fetchHierarchy({ rack_id: row.rack_id });
              opts[wh._id].bins = binData.bins || [];
            }
          }
        } catch { }
      }
      setLocationOptions(opts);
    }
    loadLocationOptions();
    // eslint-disable-next-line
  }, [isEditMode, editIndex, warehouses]);
  async function fetchHierarchy(payload: object) {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/hierarchy`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to fetch hierarchy");
      return res.json();
    } catch (error: any) {
      await handleApiError(error, {
        module: "inventory",
        action: "Signage_Add_Variable_Product_fetch_hierarchy",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/warehouses/hierarchy`,
        method: "POST",
        requestBody: payload,
      }, token);
      throw error;
    }
  }

  const handleZoneChange = async (
    ci: number,
    wi: number,
    zoneId: string,
    warehouseId: string,
    zoneLabel: string
  ) => {
    const row = combinations[ci]?.warehouseData?.[wi];
    if (!row) return;

    if (!zoneId) {
      setCombinations(prev =>
        prev.map((c, i) =>
          i === ci
            ? {
              ...c,
              warehouseData: c.warehouseData.map((w, j) =>
                j === wi
                  ? {
                    ...w,
                    zone_id: "",
                    zone_name: "",
                    rack_id: "",
                    rack_name: "",
                    bin_id: "",
                    bin_name: "",
                  }
                  : w
              ),
            }
            : c
        )
      );
      setLocationOptions(prev => ({
        ...prev,
        [warehouseId]: {
          ...prev[warehouseId],
          racks: [],
          bins: [],
        },
      }));
      return;
    }

    if (row.zone_id === zoneId) return;

    rackFetchTargetByWarehouseRef.current[warehouseId] = zoneId;

    setCombinations(prev =>
      prev.map((c, i) =>
        i === ci
          ? {
            ...c,
            warehouseData: c.warehouseData.map((w, j) =>
              j === wi
                ? {
                  ...w,
                  zone_id: zoneId,
                  zone_name: zoneLabel,
                  rack_id: "",
                  rack_name: "",
                  bin_id: "",
                  bin_name: "",
                }
                : w
            ),
          }
          : c
      )
    );

    setLocationOptions(prev => ({
      ...prev,
      [warehouseId]: {
        ...prev[warehouseId],
        racks: [],
        bins: [],
      },
    }));

    const isFetching = fetchingRacksRef.current.has(zoneId);
    if (isFetching) return;

    fetchingRacksRef.current.add(zoneId);
    try {
      const rackData = await fetchHierarchy({ zone_id: zoneId });
      if (rackFetchTargetByWarehouseRef.current[warehouseId] !== zoneId) return;
      setLocationOptions(prev => ({
        ...prev,
        [warehouseId]: {
          ...prev[warehouseId],
          racks: rackData.racks || [],
          bins: [],
        },
      }));
    } catch (err) {
      safeConsole.error("Error fetching racks:", err);
    } finally {
      fetchingRacksRef.current.delete(zoneId);
    }
  };

  const handleRackChange = async (
    ci: number,
    wi: number,
    rackId: string,
    warehouseId: string,
    rackLabel: string
  ) => {
    // ✅ Guard: Don't process if rack_id is empty
    if (!rackId) return;

    // ✅ Check if this rack is already the current one
    const currentRackId = combinations[ci]?.warehouseData?.[wi]?.rack_id || "";
    if (rackId === currentRackId) return;

    // ✅ Check if already fetching bins for this rack
    const isFetching = fetchingBinsRef.current.has(rackId);
    if (isFetching) return;

    // ✅ Update state first
    setCombinations(prev =>
      prev.map((c, i) =>
        i === ci
          ? {
            ...c,
            warehouseData: c.warehouseData.map((w, j) =>
              j === wi
                ? {
                  ...w,
                  rack_id: rackId,
                  rack_name: rackLabel,
                  bin_id: '',
                  bin_name: '',
                }
                : w
            ),
          }
          : c
      )
    );

    // ✅ Always fetch bins for selected rack (when rack changes, even if bins were previously loaded)
    fetchingBinsRef.current.add(rackId);
    try {
      const binData = await fetchHierarchy({ rack_id: rackId });
      setLocationOptions(prev => ({
        ...prev,
        [warehouseId]: {
          ...prev[warehouseId],
          bins: binData.bins || [],
        },
      }));
    } catch (err) {
      safeConsole.error("Error fetching bins:", err);
    } finally {
      fetchingBinsRef.current.delete(rackId);
    }
  };
  // ✅ Brands and categories are now loaded via useBrandCategorySelector hook above
  // No separate fetching needed!

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/warehouses?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to get warehouse data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        setWarehouses(Array.isArray(result) ? result : []);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Add_Variable_Product_fetch_warehouses",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/warehouses?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
        setWarehouses([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouses();
  }, [token]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/product-attributes?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to get attribute data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        setAttributes(Array.isArray(result) ? result : []);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Add_Variable_Product_fetch_attributes",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/product-attributes?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [token]);

  useEffect(() => {
    const fetchTaxGroups = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/tax-groups?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to get tax group data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        setTaxGroups(Array.isArray(result) ? result : []);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Add_Variable_Product_fetch_tax_groups",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/tax-groups?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTaxGroups();
  }, [token]);

  useEffect(() => {
    const fetchTaxRules = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/organizations/${organizationId}/tax-rules?permissions=false`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const result: any = await response.json();
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to get tax rule data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        setTaxRules(Array.isArray(result) ? result : []);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Add_Variable_Product_fetch_tax_rules",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/tax-rules?permissions=false`,
          method: "GET",
        }, token);
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchTaxRules();
  }, [token, userId, licenseType, userName]);

  const handleAttributeClick = (attribute: string, value: string) => {
    setOptions((prevOptions) => {
      const updatedOptions = [...prevOptions];
      const existingIndex = updatedOptions?.findIndex(
        (option) => option[attribute.toLowerCase()] === value
      );
      if (existingIndex !== -1) {
        updatedOptions.splice(existingIndex, 1); // Remove if already exists
      } else {
        updatedOptions.push({ [attribute.toLowerCase()]: value });
      }
      return updatedOptions;
    });
  };

  const [combinations, setCombinations] = useState<CombinationRow[]>([]);

  type UploadedImage = { id: string; name: string; size: number };

  const [uploaded, setUploaded] = useState<UploadedImage[]>([]);

  // already in your file
  const dataForMap = useMemo(() => (
    uploaded.length
      ? [{ product: { variants: [{ images: uploaded.map(img => ({ url: img.id })) }] } }]
      : []
  ), [uploaded]);

  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

  // Keep this shape
  type PreviewFile = { file: File; url: string };

  // Only preview locally
  const onPickFiles = (files: File[]) => {
    const existing = new Set(previewFiles.map(p => `${p.file.name}|${p.file.size}`));
    const fresh = (files || []).filter(f => !existing.has(`${f.name}|${f.size}`));
    if (!fresh.length) return;

    setPreviewFiles(prev => [
      ...prev,
      ...fresh.map(f => ({ file: f, url: URL.createObjectURL(f) } as PreviewFile)),
    ]);
  };


  // console.log(combinations, "combinations");
  useEffect(() => {
    setCombErrors(
      combinations.map(c => c.warehouseData.map(() => ({ zone: false, rack: false, bin: false })))
    );
  }, [combinations]);


  const handleGenerateCombinations = () => {
    // { size: ["24x60","36x60"], material: ["Styrene"] }
    const attributeValues = (Array.isArray(attributes) ? attributes : []).reduce<Record<string, string[]>>((acc, attr) => {
      const key = (attr?.name || "").toLowerCase();
      const selectedValues =
        (attr?.values || []).filter((value) =>
          options.some((option) => option[key] === value)
        ) || [];
      if (selectedValues.length > 0) acc[key] = selectedValues;
      return acc;
    }, {});

    const keys = Object.keys(attributeValues);
    if (keys.length === 0) {
      showError("No attributes selected.");
      return;
    }

    // seed per-variant dims from parent form
    const defaultDims = {
      length: Number(formik.values.length) || 0,
      width: Number(formik.values.width) || 0,
      height: Number(formik.values.height) || 0,
      weight: Number(formik.values.weight) || 0,
      dimension_unit: "Inches",
      weight_unit: "Pounds",
    };

    const generateCombinations = (
      remainingKeys: string[],
      currentCombination: Record<string, string>
    ): CombinationRow[] => {
      // base case → emit one combination
      if (remainingKeys.length === 0) {
        const row: CombinationRow = {
          variant_name: "",
          sku: "",
          description: "",
          attributes: currentCombination,
          price: 0,
          ...defaultDims, // ⬅️ ensures the 4 fields are present
          warehouseData: (warehouses || []).map((w) => ({
            warehouse_id: w._id,
            warehouse_name: w.label,
            zone_id: "",
            zone_name: "",
            rack_id: "",
            rack_name: "",
            bin_id: "",
            bin_name: "",
            on_hand: 0,
            available: 0,
            reserved: 0,
          })),
        };
        return [row];
      }

      // recursive case
      const [key, ...restKeys] = remainingKeys;
      const values = attributeValues[key] || [];
      let result: CombinationRow[] = [];

      for (const value of values) {
        result = result.concat(
          generateCombinations(restKeys, { ...currentCombination, [key]: value })
        );
      }

      return result;
    };

    // generate all combos
    const allCombinations: CombinationRow[] = generateCombinations(keys, {});

    // add SKUs (type stays CombinationRow because we spread comb)
    const withSkus: CombinationRow[] = allCombinations.map((comb, index) => ({
      ...comb,
      sku: formik.values.sku ? `${formik.values.sku}-${index + 1}` : "",
    }));

    setCombinations(withSkus);
    // showSuccess("Combinations generated successfully!");
  };

  // map ONE UI combination → API variant payload
  const mapCombinationRowToVariantPayload = (comb: CombinationRow, values: FormValues, opts?: { includeId?: boolean }) => {
    const isPerVariant = values.shipping_config === "per_variant";
    const dims = isPerVariant
      ? {
        length: Number((comb as any).length || 0),
        height: Number((comb as any).height || 0),
        width: Number((comb as any).width || 0),
        weight: Number((comb as any).weight || 0),
        weight_type: "kg",
        dimension_unit: "Inches",
        weight_unit: "Pounds",
      }
      : {
        length: Number(values.length || 0),
        height: Number(values.height || 0),
        width: Number(values.width || 0),
        weight: Number(values.weight || 0),
        weight_type: "kg",
        dimension_unit: "Inches",
        weight_unit: "Pounds",
      };

    const selIds = comb.imageIds || [];
    const images = selIds.map((id, i) => ({
      url: id,
      alt_text: uploaded.find(u => u.id === id)?.name || "",
      default: i === 0,
    }));
    return {
      variant_name: `${values.name} - ${Object.values(comb.attributes).join(" - ")}`,
      ...(opts?.includeId && comb.variant_id ? { variant_id: comb.variant_id } : {}), // ✅ only include for PATCH
      sku: comb.sku || "",
      description: values.description || "",
      attributes: comb.attributes || {},
      purchase_price: Number(values.purchase_price || 0),
      retail_price: Number(comb.price || values.retail_price || 0),
      images,
      package_dimensions: dims,
      inventory:
        (comb.warehouseData || [])
          .filter(
            (wd) =>
              wd &&
              wd.warehouse_id &&
              wd.warehouse_name &&
              Number(wd.available) > 0 &&
              wd.zone_id &&
              wd.rack_id &&
              wd.bin_id
          )
          .map((wd) => ({
            warehouse_id: wd.warehouse_id,
            warehouse_name: wd.warehouse_name,
            zone_id: wd.zone_id,
            zone_name: wd.zone_name,
            rack_id: wd.rack_id,
            rack_name: wd.rack_name,
            bin_id: wd.bin_id,
            bin_name: wd.bin_name,
            on_hand: Number(wd.on_hand) || Number(wd.available) || 0,
            available: Number(wd.available) || 0,
            reserved: Number(wd.reserved) || 0,
          })) || [],
      meta_data: { slug: createSlug(`${values.name} - ${Object.values(comb.attributes).join(" - ")}`) },
      customizations: {
        template_name: values.name || "",
        fields:
          customFields?.map((f) => ({
            field_name: f.label,
            field_type: f.type,
            required: f.required,
            value: "",
          })) || [],
      },
    };
  };

  // POST each combination → create variants
  // POST each combination → create variants
  const handleCreateVariantsFromCombinations = async (source?: CombinationRow[]) => {
    try {
      const createdAll: any[] = [];
      if (!token || !organizationId) return showError("Not authenticated");
      if (!productId) return showError("Please save draft first.");

      const combos = Array.isArray(source) ? source : combinations;
      if (!Array.isArray(combos) || combos.length === 0) {
        return showError("Please generate combinations first.");
      }

      const bodies = combos.map((comb) => mapCombinationRowToVariantPayload(comb, formik.values));

      if(! organizationId || !productId) {
        return showError("User ID or Product ID is missing.");
      }

      for (let i = 0; i < bodies.length; i++) {
        const variantPayload = bodies[i];
        try {
          const res = await fetch(
            `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(variantPayload),
            }
          );
          const result = await res.json();
          if (!res.ok || result?.error) {
            const msg = result?.error || result?.message || "Failed to create variant";
            throw new Error(msg);
          }

          const vid =
            result?.variant?.id ||
            result?.variant?._id ||
            result?.id ||
            result?._id ||
            result?.variant_id;

          if (vid) {
            // stamp id back into the *same* list you passed (or state if using it)
            // setCombinations((prev) =>
            //   prev.map((c, idx) => (idx === i ? { ...c, variant_id: String(vid) } : c))
            // );
            const created =
              Array.isArray(result) ? result[0]
                : (Array.isArray(result?.data) ? result.data[0] : result);

            createdAll.push(created);
            setCombinations(prev =>
              prev.map(r =>
                r.sku === (created?.sku ?? "")
                  ? { ...r, variant_id: String(created?.variant_id || created?.id || "") }
                  : r
              )
            );
            setCombinationsFetch(prev => [
              ...prev,
              {
                id: (created?.id || created?.variant_id) as string,
                attributes: created?.attributes ?? {},
                value: created?.variant_name ?? "",
                label: created?.variant_id ?? "",
                sku: created?.sku ?? "",
              },
            ]);
          }
        } catch (variantError: any) {
          await handleApiError(variantError, {
            module: "inventory",
            action: "Signage_Add_Variable_Product_create_variant",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants`,
            method: "POST",
            requestBody: variantPayload,
          }, token);
          throw variantError;
        }
      }
      fetchVariants();
      showSuccess("Variants generated successfully.");
    } catch (e: any) {
      safeConsole.error(e);
      showError(e?.message || "Failed to generate variants");
    }
  };

  const handleGenerateAndCreate = async () => {
    // 1) Build combos
    const attributeValues = (Array.isArray(attributes) ? attributes : []).reduce<Record<string, string[]>>((acc, attr) => {
      const key = (attr?.name || "").toLowerCase();
      const selectedValues =
        (attr?.values || []).filter((value) => options.some((option) => option[key] === value)) || [];
      if (selectedValues.length > 0) acc[key] = selectedValues;
      return acc;
    }, {});

    const keys = Object.keys(attributeValues);
    if (keys.length === 0) {
      showError("No attributes selected.");
      return;
    }

    const defaultDims = {
      length: Number(formik.values.length) || 0,
      width: Number(formik.values.width) || 0,
      height: Number(formik.values.height) || 0,
      weight: Number(formik.values.weight) || 0,
      dimension_unit: "Inches",
      weight_unit: "Pounds",
    };
    const defaultPrice = Number(formik.values.retail_price) || 0;

    const generateCombinations = (
      remainingKeys: string[],
      currentCombination: Record<string, string>
    ): CombinationRow[] => {
      if (remainingKeys.length === 0) {
        const row: CombinationRow = {
          variant_name: "",
          sku: "",
          description: "",
          attributes: currentCombination,
          price: defaultPrice,
          ...defaultDims,
          warehouseData: (warehouses || []).map((w) => ({
            warehouse_id: (w as any)._id,
            warehouse_name: (w as any).label,
            zone_id: "",
            zone_name: "",
            rack_id: "",
            rack_name: "",
            bin_id: "",
            bin_name: "",
            on_hand: 0,
            available: 0,
            reserved: 0,
          })),
        };
        return [row];
      }

      const [key, ...restKeys] = remainingKeys;
      const values = attributeValues[key] || [];
      let result: CombinationRow[] = [];
      for (const value of values) {
        result = result.concat(
          generateCombinations(restKeys, { ...currentCombination, [key]: value })
        );
      }
      return result;
    };

    const allCombinations: CombinationRow[] = generateCombinations(keys, {});
    const withSkus: CombinationRow[] = allCombinations.map((comb, index) => ({
      ...comb,
      sku: formik.values.sku ? `${formik.values.sku}-${index + 1}` : "",
    }));
    // 2) Update UI immediately
    setCombinations(withSkus);
    showSuccess("Combinations generated successfully!");

    // 3) Create variants using the *local* array to avoid the async state race
    await handleCreateVariantsFromCombinations(withSkus);
  };



  // PATCH the currently edited combination (single variant)
  const handleSaveEditedCombination = async () => {
    if (editIndex === null) return;
    const rowUI = combinations[editIndex] as any;

    try {
      if (!token || !organizationId) return showError("Not authenticated");
      if (!productId) return showError("Missing product");
      if(! rowUI?.variant_id) return showError("Combination must be linked to an existing variant to save edits.");
      // if (!rowUI?.variant_id) return showError("This combination is not linked to a variant yet. Generate variants first.");

      const payload = mapCombinationRowToVariantPayload(rowUI, formik.values);
      // NOTE: server identifies which variant via path parameter; usually better to omit id in body
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants/${rowUI.variant_id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await res.json();
      if (!res.ok || result?.error) {
        const msg = result?.error || result?.message || "Failed to update variant";
        throw new Error(msg);
      }



      showSuccess("Combination saved.");
      setIsEditMode(false);
      setEditIndex(null);

      // optional: refresh local values from server or keep your local state
      // await loadVariants(productId);
    } catch (e: any) {
      await handleApiError(e, {
        module: "inventory",
        action: "Signage_Add_Variable_Product_update_variant",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants/${rowUI.variant_id}`,
        method: "PATCH",
        requestBody: mapCombinationRowToVariantPayload(rowUI, formik.values),
      }, token);
      safeConsole.error(e);
      showError(e?.message || "Failed to save combination");
    }
  };




  const createSlug = (input: string): string => {
    return input
      .toLowerCase() // Convert to lowercase
      .trim() // Remove leading and trailing whitespace
      .replace(/[^a-z0-9\s-]/g, "") // Remove invalid characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-"); // Replace multiple hyphens with a single one
  };

  const generateProductNames = (
    productName: string,
    options: { [key: string]: string }[]
  ) => {
    return options?.map((option) => {
      const formattedAttributes = Object.values(option)
        .map((value) => value.trim()) // Trim whitespace
        .join(" - "); // Combine attributes with " - "
      return `${productName} - ${formattedAttributes}`;
    });
  };



  const generateProductVariants = async (
    values: FormValues,     // snapshot of previewFiles for alt_text
  ) => {
    const result = await Promise.all(
      (combinations || []).map(async (comb, index) => {
        const variantName = `${values.name} - ${Object.values(comb.attributes).join(" - ")}`;
        const dims =
          values.shipping_config === "per_variant"
            ? {
              length: Number((comb as any).length || 0),
              height: Number((comb as any).height || 0),
              width: Number((comb as any).width || 0),
              weight: Number((comb as any).weight || 0),
              weight_type: "kg",
              dimension_unit: "Inches",
              weight_unit: "Pounds",
            }
            : {
              length: Number(values.length || 0),
              height: Number(values.height || 0),
              width: Number(values.width || 0),
              weight: Number(values.weight || 0),
              weight_type: "kg",
              dimension_unit: "Inches",
              weight_unit: "Pounds",
            };
        const selIds = (comb.imageIds || []);
        const images = selIds.map((id, i) => ({
          url: id,
          alt_text: uploaded.find(u => u.id === id)?.name || "",
          default: i === 0,
        }));

        return {
          variant_name: variantName,
          variant_id: comb.variant_id,
          sku: comb.sku, // Add SKU differentiation
          description: values.description,
          attributes: comb.attributes,
          purchase_price: Number(values.purchase_price),
          retail_price: Number(comb.price),
          images,
          package_dimensions: dims,
          inventory:
            (comb.warehouseData || [])
              .filter(
                (wd) =>
                  wd &&
                  wd.warehouse_id &&
                  wd.warehouse_name &&
                  Number(wd.available) > 0
              )
              .map((wd) => ({
                warehouse_id: wd.warehouse_id,
                warehouse_name: wd.warehouse_name,
                zone_id: wd.zone_id,
                zone_name: wd.zone_name,
                rack_id: wd.rack_id,
                rack_name: wd.rack_name,
                bin_id: wd.bin_id,
                bin_name: wd.bin_name,
                on_hand: Number(wd.on_hand) || Number(wd.available) || 0,
                available: Number(wd.available) || 0,
                reserved: Number(wd.reserved) || 0,
              })) || [],
          meta_data: {
            slug: createSlug(variantName),
          },
          customizations: {
            template_name: values.name,
            fields:
              customFields?.map((f) => ({
                field_name: f.label,
                field_type: f.type,
                required: f.required,
                value: "",
              })) || [],
          },
        };
      })
    );
    return result;
  };

  const handlePrepareProductImages = async () => {
    if (imagesPreparing) return;
    setImagesPreparing(true);

    try {
      if (pendingDeleteIds.length > 0) {
        await hardDeletePresignedFiles(pendingDeleteIds, token || null);
        setPendingDeleteIds([]); // clear
      }
    } catch (delErr: any) {
      safeConsole.error("Image hard delete (batch) failed:", delErr);
      // Don't block success — just inform
      showError(delErr?.message || "Some removed images could not be deleted.");
    }
    try {
      // 1) Upload newly picked images (if any)
      const stagedFiles = previewFiles.map((p) => p.file);
      let stagedIds: string[] = [];
      if (stagedFiles.length) {
        if (!token) throw new Error("Not authenticated");
        stagedIds = await uploadFilesAndGetIds(stagedFiles, token);
      }

      // 2) Build merged uploaded list
      const stagedUploaded = stagedIds.map((id, i) => ({
        id,
        name: stagedFiles[i].name,
        size: stagedFiles[i].size,
      }));
      const allUploaded = [...uploaded, ...stagedUploaded];

      // 3) Build productImages array
      const productImages = allUploaded.map((u, i) => ({
        url: u.id,
        alt_text: u.name,
        default: i === 0,
      }));

      // 4) Store into Formik so onSubmit / handleSaveDraft can read it
      await formik.setFieldValue("images", productImages, false);
      // (optional) also set the primary "image" field if backend expects it
      await formik.setFieldValue("image", productImages[0] ? { url: productImages[0].url } : undefined, false);

      // 5) Clear preview URLs
      previewFiles.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch { } });
      setPreviewFiles([]);

      // 6) If you keep a local “uploaded” list in state, update it too
      // so next time you won't reupload the same files.
      setUploaded(allUploaded as any);

      setImagesPrepared(true);
      showSuccess("Images prepared. You can save now.");
    } catch (e: any) {
      safeConsole.error(e);
      showError(e?.message || "Failed to prepare images.");
    } finally {
      setImagesPreparing(false);
    }
  };


  const formik = useFormik<FormValues>({
    initialValues: {
      name: "",
      product_type: "variable",
      sku: "",
      description: "",
      brand: "",
      category: "",
      item_condition: "",
      condition_note: "",
      purchase_price: "",
      retail_price: "",
      length: "",
      height: "",
      width: "",
      weight: "",
      threshold_qty: "",
      attribute: "",
      parameter: "",
      notes: "",
      images: null,
      statuss: "Active",
      is_rotational: "",
      is_production: "",
      warehouseId: "",
      on_hand: "0",
      available: "0",
      reserved: "0",
      is_taxable: false,
      tax_rule_id: "",
      taxRule: null,
      taxGroup: null,
      shipping_config: "global",
    },
    validationSchema: Yup.object({
      name: nameValidation,
      sku: Yup.string().required("SKU is required"),
      retail_price: Yup.number().required("Retail price is required"),
      brand: Yup.string().required("Brand is required"),
      category: Yup.string().required("Category is required"),
      // is_rotational: Yup.string().required("Is Rotational is required"),
      is_production: Yup.string().required("Is Production is required"),
      // statuss: statusValidation,
    }),
    onSubmit: async (values) => {
      const isGlobalShipping = values.shipping_config === "global";
      setFormSubmitting(true);

      // try {
      //   if (pendingDeleteIds.length > 0) {
      //     await hardDeletePresignedFiles(pendingDeleteIds, token || null);
      //     setPendingDeleteIds([]); // clear
      //   }
      // } catch (delErr: any) {
      //   safeConsole.error("Image hard delete (batch) failed:", delErr);
      //   // Don't block success — just inform
      //   showError(delErr?.message || "Some removed images could not be deleted.");
      // }

      // const stagedFiles = previewFiles.map(p => p.file);
      // let stagedIds: string[] = [];
      // if (stagedFiles.length) {
      //   stagedIds = await uploadFilesAndGetIds(stagedFiles, token || null);
      // }

      // // Merge with existing uploaded for payload
      // const stagedUploaded = stagedIds.map((id, i) => ({ id, name: stagedFiles[i].name, size: stagedFiles[i].size }));
      // const allUploaded = [...uploaded, ...stagedUploaded];

      // // …build productImages from allUploaded…

      // // After the product save succeeds:
      // previewFiles.forEach(p => { try { URL.revokeObjectURL(p.url); } catch { } });
      // setPreviewFiles([]);

      // const productImages = allUploaded.map((u, i) => ({
      //   url: u.id,
      //   alt_text: u.name,
      //   default: i === 0,
      // }));
      const productImages = Array.isArray(formik.values.images) ? formik.values.images : [];
      const productVariants = await generateProductVariants(values);;
      if (productVariants.length === 0) {
        showError("Please add combinations.");
        setFormSubmitting(false);
      }

      const errors = combinations.map(c =>
        c.warehouseData.map(() => ({ zone: false, rack: false, bin: false }))
      );
      let hasError = false;

      const hasAnyQuantity = combinations.some(comb =>
        comb.warehouseData.some(row => row.available > 0)
      );

      if (!hasAnyQuantity) {
        showInfo("Enter quantity for at least one warehouse.");
        return;
      }

      // 2. Mark any missing selects when available > 0
      combinations.forEach((comb, ci) => {
        comb.warehouseData.forEach((row, wi) => {
          if (row.available > 0) {
            if (!row.zone_id) { errors[ci][wi].zone = true; hasError = true; }
            if (!row.rack_id) { errors[ci][wi].rack = true; hasError = true; }
            if (!row.bin_id) { errors[ci][wi].bin = true; hasError = true; }
          }
        });
      });

      // 3. If any errors, push them into state (shows inline feedback) and abort
      if (hasError) {
        showInfo("Please select a zone, rack and bin for you selected warehouse combination.");
        setCombErrors(errors);
        return;
      }

      let updatePayload: any = undefined;
      try {
        const selectedTaxRule = taxRules?.find((rule) => rule._id === values.tax_rule_id);

        updatePayload = {
          inventory_type: "signage",
          name: values.name,
          product_type: values.product_type,
          sku: values.sku,
          description: values.description,
          brand: selectedBrandName,  // ✅ Send brand NAME (not ID)
          brand_id: selectedbrandId,  // ✅ Send brand ID (not name)
          category: selectedCategoryName,  // ✅ Send category NAME (not ID)
          category_id: selectedCategoryId || null,  // ✅ Send category ID (not name)
          item_condition: values.item_condition,
          condition_note: values.condition_note,
          purchase_price: Number(values.purchase_price),
          retail_price: Number(values.retail_price),
          ...(values.shipping_config === "global"
            ? {
              package_dimensions: {
                length: Number(values.length || 0),
                height: Number(values.height || 0),
                width: Number(values.width || 0),
                weight: Number(values.weight || 0),
                dimension_unit: "Inches",
                weight_unit: "Pounds",
              },
            }
            : {}),
          shipping_config: isGlobalShipping,
          variants: productVariants.length > 0 ? productVariants : null,
          threshold_qty: Number(values.threshold_qty),
          images: productImages,
          status: values.statuss,
          is_rotational: values.is_rotational == "yes" ? true : false,
          is_production: values.is_production == "yes" ? true : false,
          production_status: values.is_production === "yes" ? "production" : "stock",
          is_taxable: values.is_taxable,
          tax_rule: values.is_taxable && values.taxRule ? values.taxRule : null,
          tax_group: values.is_taxable && values.taxGroup ? values.taxGroup : null,
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updatePayload),
          }
        );

        const result: any = await response.json();
        setProductId(result?.product_id);
        if (!response.ok || result?.error) {
          const message = result?.error || result?.message || "Failed to save data";
          safeConsole.error("API Error:", message);
          showError(message);
          throw new Error(message);
        }
        showSuccess("Product added successfully!");

        safeNavigate(`${baseRoute}/products`);
      } catch (err: any) {
        await handleApiError(err, {
          module: "inventory",
          action: "Signage_Add_Variable_Product_update_product",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}`,
          method: "PATCH",
          requestBody: updatePayload,
        }, token);
        safeConsole.error(err instanceof Error ? err.message : "Unknown error");
        // toast.error(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setFormSubmitting(false);
      }
    },
  });

  // ✅ Brand options from hook
  const brandOptions = useMemo(
    () => brandOptionsFromHook,
    [brandOptionsFromHook]
  );

  // ✅ Category options from hook (filtered by selected brand)
  const categoryOptions = useMemo(
    () => {
      // ✅ Only return categories if brand is selected
      return selectedBrand ? categoryOptionsFromHook : [];
    },
    [categoryOptionsFromHook, selectedBrand]
  );

  // ✅ Refs to prevent circular updates and track previous values
  const isSyncingFromFormik = useRef(false);
  const isSyncingFromHook = useRef(false);
  const prevFormikBrand = useRef<string>("");
  const prevFormikCategory = useRef<string>("");
  const prevSelectedBrand = useRef<string>("");
  const prevSelectedCategory = useRef<string>("");

  // ✅ Monitor formik brand changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const brandValue = formik.values.brand || "";
    if (brandValue && brandValue !== prevFormikBrand.current && brandValue !== selectedBrand) {
      prevFormikBrand.current = brandValue;
      isSyncingFromFormik.current = true;
      setSelectedBrand(brandValue);
      setSelectedCategories([]); // Clear categories when brand changes
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.brand, selectedBrand, setSelectedBrand, setSelectedCategories]);

  // ✅ Monitor formik category changes and sync to hook (Formik → Hook)
  useEffect(() => {
    if (isSyncingFromHook.current) {
      isSyncingFromHook.current = false;
      return;
    }

    const categoryValue = formik.values.category || "";
    const currentFirst = selectedCategories[0] || "";
    if (categoryValue && categoryValue !== prevFormikCategory.current && categoryValue !== currentFirst) {
      prevFormikCategory.current = categoryValue;
      isSyncingFromFormik.current = true;
      setSelectedCategories([categoryValue]);
      setTimeout(() => { isSyncingFromFormik.current = false; }, 0);
    }
  }, [formik.values.category, selectedCategories, setSelectedCategories]);

  // ✅ Monitor hook brand changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    if (selectedBrand && selectedBrand !== prevSelectedBrand.current && formik.values.brand !== selectedBrand) {
      prevSelectedBrand.current = selectedBrand;
      isSyncingFromHook.current = true;
      formik.setFieldValue("brand", selectedBrand, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    }
  }, [selectedBrand, formik.values.brand]);

  // ✅ Monitor hook category changes and sync to formik (Hook → Formik)
  useEffect(() => {
    if (isSyncingFromFormik.current) {
      return;
    }

    const firstCategory = selectedCategories[0] || "";
    if (firstCategory && firstCategory !== prevSelectedCategory.current && formik.values.category !== firstCategory) {
      prevSelectedCategory.current = firstCategory;
      isSyncingFromHook.current = true;
      formik.setFieldValue("category", firstCategory, false);
      setTimeout(() => { isSyncingFromHook.current = false; }, 0);
    } else if (!firstCategory && formik.values.category) {
      // Clear category if hook has no selection
      if (prevSelectedCategory.current !== "") {
        prevSelectedCategory.current = "";
        isSyncingFromHook.current = true;
        formik.setFieldValue("category", "", false);
        setTimeout(() => { isSyncingFromHook.current = false; }, 0);
      }
    }
  }, [selectedCategories, formik.values.category]);

  // Extract brand and category names and IDs from options for onSubmit
  const selectedBrandOption = brandOptions.find(b => b.value === (formik.values.brand || selectedBrand));
  const selectedCategoryOption = categoryOptions.find(c => c.value === (formik.values.category || selectedCategories[0]));
  const selectedBrandName = selectedBrandOption?.label || "";
  const selectedbrandId = selectedBrandOption?.value || ""; // ✅ Always use ID from option, not formik value
  const selectedCategoryName = selectedCategoryOption?.label || "";
  const selectedCategoryId = selectedCategoryOption?.value || ""; // ✅ Always use ID from option, not formik value

  const PAGE_SIZE = 10;
  const effectiveUserId = userId || "";
  type SelectOptionType = { value: string; label: string };
  type AdditionalType = { page?: number };

  const loadBrandOptions: LoadOptions<SelectOptionType, GroupBase<SelectOptionType>, AdditionalType> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const currentPage = additional?.page ?? 1;
      try {
        const resp = await fetchBrandsByUserSelect2(membershipId ?? "", token || "", {
          q: (inputValue || "").trim(),
          page: currentPage,
          limit: PAGE_SIZE,
        });
        const options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Brand"),
        }));
        const hasMore = !!resp.pagination?.more;
        return { options, hasMore, additional: { page: currentPage + 1 } };
      } catch {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
    },
    [effectiveUserId, token]
  );

  const loadCategoryOptions: LoadOptions<SelectOptionType, GroupBase<SelectOptionType>, AdditionalType> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      if (!selectedBrand) return { options: [], hasMore: false, additional: { page: 1 } };
      const currentPage = additional?.page ?? 1;
      try {
        const resp = await fetchCategoriesByUserSelect2(membershipId ?? "", token || "", {
          q: (inputValue || "").trim(),
          page: currentPage,
          limit: PAGE_SIZE,
        });
        let options = (resp.results || []).map((item: any) => ({
          value: String(item?.id ?? item?._id ?? ""),
          label: String(item?.text ?? item?.name ?? "Unnamed Category"),
        }));
        if (brandCategoryIds?.length) {
          const idSet = new Set(brandCategoryIds);
          options = options.filter((o) => idSet.has(o.value));
        }
        const hasMore = !!resp.pagination?.more;
        return { options, hasMore, additional: { page: currentPage + 1 } };
      } catch {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
    },
    [effectiveUserId, token, selectedBrand, brandCategoryIds]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const combinationsPerPage = 10000;

  const indexOfLastCombination = currentPage * combinationsPerPage;
  const indexOfFirstCombination = indexOfLastCombination - combinationsPerPage;
  const paginatedCombinations = combinations.slice(indexOfFirstCombination, indexOfLastCombination);

  const totalPages = Math.ceil(combinations.length / combinationsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const is24Hex = (s?: string) => typeof s === "string" && /^[a-f0-9]{24}$/i.test(s);

  async function deleteVariantFromServer(variantId: string) {
    try {
      const url = `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants/${variantId}`;
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Failed to delete variant");
      }
    } catch (error: any) {
      await handleApiError(error, {
        module: "inventory",
        action: "Signage_Add_Variable_Product_delete_variant",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants/${variantId}`,
        method: "DELETE",
      }, token);
      throw error;
    }
  }

  const mobileColumns: ColumnDef<CombRow, any>[] = [
    columnHelper.display({
      id: "sno",
      header: () => "SNO",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor(
      (row) => Object.entries(row.attributes).map(([k, v]) => `${k}: ${v}`).join(", "),
      {
        id: "combination",
        header: () => "Combination",
        cell: (info) => <div className="text-wrap">{info.getValue()}</div>,
      }
    ),
    columnHelper.display({
      id: "price",
      header: () => "Price",
      cell: (info) => {
        const idx = info.row.index;
        const value = combinations[idx]?.price ?? 0;
        return (
          <input
            type="number"
            className="form-control"
            placeholder="0"
            value={value === 0 ? "" : value}
            onChange={(e) => {
              const price = parseFloat(e.target.value) || 0;
              setCombinations((prev: any[]) => prev.map((c, i) => (i === idx ? { ...c, price } : c)));
            }}
          />
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => "Action",
      cell: (info) => {
        const idx = info.row.index;
        const row = info.row.original as CombinationRow;

        // Prefer real variant id; only treat as "saved" if it's 24-hex
        const variantId = String(row?.variant_id ?? row?.id ?? "");
        const hasServerId = is24Hex(variantId);
        // delKey drives spinner/disabled; use server id if present, else a tmp key per row
        const delKey = hasServerId ? variantId : `tmp-${idx}`;

        return (
          <div className="d-flex gap-2">
            <EditIconButton
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setIsEditMode(true); setEditIndex(idx); }}
              title="Edit"
            />
            <DeleteIconButton
              type="button"
              className="btn btn-link text-danger p-0"
              title="Remove"
              disabled={deletingIds.has(delKey)}
              onClick={async () => {
                setDeletingIds(prev => new Set(prev).add(delKey));
                try {
                  if (hasServerId) {
                    await deleteVariantFromServer(variantId);   // ✅ real server delete
                  }
                  // remove from UI either way
                  setCombinations(prev => prev.filter((_, i) => i !== idx));
                  showSuccess?.("Variant deleted.");
                  // optional hard re-sync:
                  // await fetchProduct();
                } catch (err: any) {
                  safeConsole.error(err);
                  showError?.(err?.message || "Failed to delete variant.");
                } finally {
                  setDeletingIds(prev => {
                    const n = new Set(prev);
                    n.delete(delKey);
                    return n;
                  });
                }
              }}
            >
              {deletingIds.has(delKey) ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              ) : (
                <i className="bi bi-trash" />
              )}
            </DeleteIconButton>
          </div>
        );
      },
    }),
  ];

  const mobileData = useMemo<CombRow[]>(
    () => (combinations || []) as CombRow[],
    [combinations]
  );

  const mobileTable = useReactTable({
    data: mobileData,
    columns: mobileColumns,
    getCoreRowModel: getCoreRowModel(),
  });


  const isProductDetailsFilled =
    !!formik.values.name &&
    !!formik.values.sku &&
    !!formik.values.retail_price &&
    formik.values.is_production !== "" &&
    !!formik.values.statuss;



  const resetGlobalDimsToZero = () => {
    formik.setFieldValue("length", 0);
    formik.setFieldValue("width", 0);
    formik.setFieldValue("height", 0);
    formik.setFieldValue("weight", 0);
  };

  const resetAllCombinationDimsToZero = () => {
    // Only matters if you’re using per-variant dims
    setCombinations(prev =>
      (prev || []).map(c => ({
        ...c,
        length: 0,
        width: 0,
        height: 0,
        weight: 0,
      }))
    );
  };


  const fetchVariants = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      if (!token || !userId) return showError("Not authenticated");
      if (!productId) return showError("Missing product");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants?permissions=false`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal,
        }
      );

      // If aborted, stop quietly
      if (signal?.aborted) return;

      const result: any = await res.json();
      if (!res.ok || result?.error) {
        const message = result?.error || result?.message || "Failed to get variants";
        throw new Error(message);
      }

      // Result can be either an array or { data: [...] }
      const raw: VariantApiItem[] = Array.isArray(result) ? result : (result?.data ?? []);

      // Normalize to your picker/combination shape
      const variantCombos = raw.map((c) => ({
        id: String(c.id || c.variant_id || ""),
        attributes: c.attributes ?? {},
        variant_name: c.variant_name ?? "",
        sku: c.sku ?? "",
      }));

      // If aborted after parse, stop
      if (signal?.aborted) return;

      setCombinationsFetch(variantCombos);
      
      // Transform variants to ProductCombination format for DesignsTab
      const transformedVariables = transformVariantsToCombinations(raw);
      setVariables(transformedVariables);
    } catch (err: any) {
      if (err?.name === "AbortError") return; // ignore aborts
      await handleApiError(err, {
        module: "inventory",
        action: "Signage_Add_Variable_Product_fetch_variants",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${organizationId}/inventory/${productId}/variants?permissions=false`,
        method: "GET",
      }, token);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !userId || !productId) return;

    const ctrl = new AbortController();
    fetchVariants(ctrl.signal);

    return () => {
      ctrl.abort();
    };
    // ✅ use correct deps; you don't need setCombinations here
  }, [token, userId, productId, variantsRefreshKey]);

  // put this inside your component (below the helpers)
  const getDefaultWarehouseChain = async (): Promise<{
    warehouse_id: string;
    warehouse_name: string;
    zone_id: string;
    zone_name: string;
    rack_id: string;
    rack_name: string;
    bin_id: string;
    bin_name: string;
  } | null> => {
    try {
      // 0) need warehouses list first
      if (!Array.isArray(warehouses) || warehouses.length === 0) return null;

      // 1) first warehouse
      const w = warehouses[0];
      const warehouse_id = String((w as any)._id || (w as any).id || "");
      const warehouse_name = String((w as any).label || (w as any).name || "");

      if (!warehouse_id) return null;

      // 2) zones under this warehouse
      const zonesResp = await fetchHierarchy({ warehouse_id });
      const zones = Array.isArray(zonesResp?.zones) ? zonesResp.zones : [];
      const z = zones[0];
      if (!z) return null;
      const zone_id = String(z._id || z.id || z.value || "");
      const zone_name = String(z.label || z.name || "");

      // 3) racks under this zone
      const racksResp = await fetchHierarchy({ zone_id });
      const racks = Array.isArray(racksResp?.racks) ? racksResp.racks : [];
      const r = racks[0];
      if (!r) return null;
      const rack_id = String(r._id || r.id || r.value || "");
      const rack_name = String(r.label || r.name || "");

      // 4) bins under this rack
      const binsResp = await fetchHierarchy({ rack_id });
      const bins = Array.isArray(binsResp?.bins) ? binsResp.bins : [];
      const b = bins[0];
      if (!b) return null;
      const bin_id = String(b._id || b.id || b.value || "");
      const bin_name = String(b.label || b.name || "");

      return { warehouse_id, warehouse_name, zone_id, zone_name, rack_id, rack_name, bin_id, bin_name };
    } catch {
      return null;
    }
  };

  // Try to pull a variant id out of the create response
  const extractFirstVariantId = (result: any): string | undefined => {
    const v0 =
      result?.variants?.[0] ||
      result?.product?.variants?.[0] ||
      result?.product?.variant ||
      result?.variant;
    return (
      v0?.variant_id ||
      v0?._id ||
      v0?.id ||
      undefined
    );
  };

  // Fallback: fetch product then read its first variant id (best-effort, non-fatal)
  const fetchFirstVariantId = async (API: string, signageId: string, productId: string, token: string) => {
     
    if (!token || !userId) throw new Error("Not authenticated");
    if (!signageId || !productId) throw new Error("Missing signage or product ID");
  
    try {
      const res = await fetch(`${API}/signage/${organizationId}/inventory/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) return undefined;
      const v0 = json?.product?.variants?.[0] || json?.variants?.[0];
      return v0?.variant_id || v0?._id || v0?.id || undefined;
    } catch (error: any) {
      await handleApiError(error, {
        module: "inventory",
        action: "Signage_Add_Variable_Product_fetch_first_variant_id",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${API}/signage/${organizationId}/inventory/${productId}?permissions=false`,
        method: "GET",
      }, token);
      return undefined;
    }
  };


  const handleSaveDraft = async () => {
    if (formSubmitting) return;
    setFormSubmitting(true);
    try {
      // 1) Upload any newly picked images (optional)
      const stagedFiles = previewFiles.map(p => p.file);
      let stagedIds: string[] = [];
      if (stagedFiles.length) {
        stagedIds = await uploadFilesAndGetIds(stagedFiles, token || null);
      }
      const stagedUploaded = stagedIds.map((id, i) => ({
        id, name: stagedFiles[i].name, size: stagedFiles[i].size
      }));
      const allUploaded = [...uploaded, ...stagedUploaded];

      // 2) Build minimal product-level images payload
      const productImages = allUploaded.map((u, i) => ({
        url: u.id,
        alt_text: u.name,
        default: i === 0,
      }));

      // 3) Resolve the 0th warehouse→zone→rack→bin chain dynamically
      const chain = await getDefaultWarehouseChain();

      // 4) Build exactly ONE default variant so backend is happy
      const isPerVariant = formik.values.shipping_config === "per_variant";
      const oneDefaultVariant = {
        variant_name: `${formik.values.name || "Default"} - Variant`,
        sku: formik.values.sku ? `${formik.values.sku}-1` : "",
        description: formik.values.description || "",
        attributes: {},
        purchase_price: Number(formik.values.purchase_price || 0),
        retail_price: Number(formik.values.retail_price || 0),
        images: [],
        package_dimensions: isPerVariant
          ? { length: 0, width: 0, height: 0, weight: 0, weight_type: "kg", dimension_unit: "Inches", weight_unit: "Pounds" }
          : {
            length: Number(formik.values.length || 0),
            width: Number(formik.values.width || 0),
            height: Number(formik.values.height || 0),
            weight: Number(formik.values.weight || 0),
            weight_type: "kg",
            dimension_unit: "Inches",
            weight_unit: "Pounds",
          },
        inventory: chain ? [{
          ...chain,
          on_hand: 1,
          available: 1,
          reserved: 0,
        }] : [],
        meta_data: { slug: (formik.values.name || "default").toLowerCase().replace(/[^a-z0-9]+/g, "-") },
        customizations: { template_name: formik.values.name || "", fields: [] },
        is_rotational: formik.values.is_rotational === "yes",
      };

      // 5) Build the product payload INCLUDING the default variant
      const body: any = {
        inventory_type: "signage",
        name: formik.values.name || "(Untitled Product)",
        product_type: formik.values.product_type, // "variable"
        sku: formik.values.sku || "",
        description: formik.values.description || "",
        brand: selectedBrandName || null,
        brand_id: selectedbrandId || null,
        category: selectedCategoryName,
        category_id: selectedCategoryId || null,
        item_condition: formik.values.item_condition || "",
        condition_note: formik.values.condition_note || "",
        purchase_price: Number(formik.values.purchase_price || 0),
        retail_price: Number(formik.values.retail_price || 0),
        ...(formik.values.shipping_config === "global"
          ? {
            package_dimensions: {
              length: Number(formik.values.length || 0),
              height: Number(formik.values.height || 0),
              width: Number(formik.values.width || 0),
              weight: Number(formik.values.weight || 0),
              dimension_unit: "Inches",
              weight_unit: "Pounds",
            },
          }
          : {}),
        shipping_config: formik.values.shipping_config === "global",
        variants: [oneDefaultVariant],  // ✅ include one default variant
        threshold_qty: Number(formik.values.threshold_qty || 0),
        images: productImages,
        status: "Draft",
        is_rotational: formik.values.is_rotational == "yes",
        is_production: formik.values.is_production == "yes",
        production_status: formik.values.is_production === "yes" ? "production" : "stock",
        is_taxable: !!formik.values.is_taxable,
        tax_rule: formik.values.is_taxable && formik.values.taxRule ? formik.values.taxRule : null,
        tax_group: formik.values.is_taxable && formik.values.taxGroup ? formik.values.taxGroup : null,
      };

      // 6) Create draft (server will now see at least one variant)
      const API = process.env.REACT_APP_API_URL!;
      let draftPayload = body;
      try {
        const res = await fetch(`${API}/signage/${organizationId}/inventory`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(draftPayload),
        });

        const result = await res.json();
        if (!res.ok || result?.error) {
          throw new Error(result?.error || result?.message || "Failed to save draft");
        }

        // --- NEW: delete the seed (0th) variant right after create ---
        const pid =
          String(result?.product_id ||
            result?.product?.id ||
            result?.product?._id ||
            result?.id ||
            result?._id);

        // Save for later flows
        if (pid) setProductId(pid);

        // Try to get the variant id from the create response
        let seedVariantId = extractFirstVariantId(result);

        // Fallback: fetch product to find first variant id
        if (!seedVariantId && pid) {
          seedVariantId = await fetchFirstVariantId(API, userId!, pid, token!);
        }

        if (pid && seedVariantId) {
          try {
            const delRes = await fetch(`${API}/signage/${organizationId}/inventory/${pid}/variants/${seedVariantId}`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` },
            });
            const delJson = await delRes.json().catch(() => ({}));
            if (!delRes.ok || delJson?.error) {
              safeConsole.warn("Seed variant delete failed:", delJson?.error || delJson?.message || delRes.status);
            }
          } catch (e) {
            safeConsole.warn("Seed variant delete threw:", e);
          } finally {
            // 🔁 trigger your useEffect to GET variants again
            setVariantsRefreshKey((k) => k + 1);
          }
        } else {
          // No seed id? Still refresh in case server normalized anything.
          setVariantsRefreshKey((k) => k + 1);
        }
        // --- end NEW ---

        // showSuccess("Draft saved.");

        // clear local previews safely
        previewFiles.forEach(p => { try { URL.revokeObjectURL(p.url); } catch { } });
        setPreviewFiles([]);
        return pid;
      } catch (draftError: any) {
        await handleApiError(draftError, {
          module: "inventory",
          action: "Signage_Add_Variable_Product_save_draft",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${API}/signage/${organizationId}/inventory`,
          method: "POST",
          requestBody: draftPayload,
        }, token);
        throw draftError;
      }
    } catch (err: any) {
      safeConsole.error(err);
      showError(err?.message || "Unable to save draft");
      return undefined;
    } finally {
      setFormSubmitting(false);
    }
  };

  const combosForDiscount = (combinationsfetch || []).map((c) => {
    const safeId = String(c.variant_id ?? c.id ?? ""); // prefer variant_id, fallback to id
    return {
      id: safeId,                         // required
      variant_id: safeId,                 // keep for convenience
      variant_name: c.variant_name || "",
      sku: c.sku || "",
      attributes: c.attributes || {},
    };
  }).filter(c => c.id); // drop any that still don't have an id

  const hasCombos =
    Array.isArray(combosForDiscount) &&
    combosForDiscount.length > 0 &&
    combosForDiscount.every(c => !!(c?.id || c?.variant_id));

  const canShowVolumeDiscount = !!productId && hasCombos;

  // Lock tabs until a draft exists
  const tabsLocked = !productId;
  const lockTip = "Create & save the product to unlock this tab";

  // Bootstrap Tab switch (reliable)
  const goToTab = (hash: string) => {
    const el = document.querySelector<HTMLAnchorElement>(`.rc-tabs a[href="${hash}"]`);
    if (!el) return;
    const bs = (window as any)?.bootstrap;
    if (bs?.Tab) new bs.Tab(el).show();
    else el.dispatchEvent(new Event("click", { bubbles: true }));
  };

  const [nextLocked, setNextLocked] = React.useState(false);


  // Init tooltips whenever lock state changes
  useEffect(() => {
    const bs = (window as any)?.bootstrap;
    const els = document.querySelectorAll('.rc-tabs [data-bs-toggle="tooltip"]');
    const instances: any[] = [];
    if (bs?.Tooltip) {
      els.forEach((el) => instances.push(new bs.Tooltip(el)));
    }
    return () => instances.forEach((t) => t?.dispose?.());
  }, [tabsLocked]);



  return (
    <div className="container-xxl py-3">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Add Variable Product</h4>
      </div>

      <ul className="nav gap-2 flex-wrap mb-4 rc-tabs" role="tablist">
        <li className="nav-item">
          <a
            id="tab-product-details"
            className="activetabs active"
            data-bs-toggle="tab"
            href="#product-details"
            role="tab"
            aria-controls="product-details"
            aria-selected="true"
          >
            Product Details
          </a>
        </li>

        {[
          { id: "images", label: "Images" },
          { id: "combinations", label: "Combinations" },
          { id: "volume-discount", label: "Volume Discount" },
          { id: "customization", label: "Customization" },
          { id: "design", label: "Design" },
        ].map(({ id, label }) => (
          <li className="nav-item" key={id}>
            <a
              id={`tab-${id}`}
              className={`activetabs ${tabsLocked ? "locked" : ""}`}
              // When unlocked → real tab; When locked → tooltip
              data-bs-toggle={tabsLocked ? "tooltip" : "tab"}
              data-bs-placement={tabsLocked ? "top" : undefined}
              data-bs-title={tabsLocked ? lockTip : undefined}
              title={tabsLocked ? lockTip : undefined}
              href={`#${id}`}
              role="tab"
              aria-controls={id}
              aria-selected="false"
              aria-disabled={tabsLocked ? "true" : "false"}
              onClick={(e) => {
                if (tabsLocked) {
                  e.preventDefault(); // block navigation while locked
                  return;
                }
                // Call fetchVariants when Design tab is clicked
                if (id === "design" && productId) {
                  fetchVariants();
                }
              }}
              onMouseDown={(e) => {
                if (tabsLocked) e.preventDefault(); // avoid focus jump on locked tabs
              }}
            >
              {label}
            </a>
          </li>
        ))}
      </ul>



      <form onSubmit={formik.handleSubmit}>
        <div className="tab-content">
          {/* ================= PRODUCT DETAILS ================= */}
          <div className="tab-pane fade show active" id="product-details" role="tabpanel">
            <div className="box-shadow-datatables shadow-border-1 p-5">
              <div className="row g-4">
                {/* LEFT */}
                <div className="col-lg-6">
                  <div className="font-medium header-color-product tracking-wide uppercase mb-4">
                    <div {...iconLabelProps(16)}>
                      <Package {...withIconStyle("#4a5565", 19)} />

                      <span className="header-color-product"> Product Information</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <UIInputFF formik={formik} name="name" label="Product Name" required placeholder="Enter product name" />
                  </div>

                  <div className="mb-3 col-md-8">
                    <label className="form-label">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <AsyncPaginate
                      classNamePrefix="rs"
                      placeholder="Search or select brand..."
                      value={
                        selectedBrandOption
                          ? { value: selectedBrandOption.value, label: selectedBrandOption.label }
                          : null
                      }
                      loadOptions={loadBrandOptions}
                      additional={{ page: 1 }}
                      onChange={(selected) => {
                        if (selected) {
                          formik.setFieldValue("brand", selected.value, true);
                          setSelectedBrand(selected.value);
                          setSelectedCategories([]);
                        } else {
                          formik.setFieldValue("brand", "", true);
                          setSelectedBrand("");
                          setSelectedCategories([]);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("brand", true)}
                      isClearable
                      className={formik.touched?.brand && formik.errors?.brand ? "is-invalid" : ""}
                      styles={{
                        control: (base: any) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: formik.touched?.brand && formik.errors?.brand ? "#dc3545" : base.borderColor,
                        }),
                      }}
                    />
                    {formik.touched?.brand && formik.errors?.brand && (
                      <div className="invalid-feedback d-block">{formik.errors.brand}</div>
                    )}
                  </div>

                  <div className="mb-3 col-md-8">
                    <label className="form-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <AsyncPaginate
                      classNamePrefix="rs"
                      placeholder={selectedBrand ? "Search or select category..." : "Select brand first"}
                      isDisabled={!selectedBrand}
                      value={
                        selectedCategoryOption
                          ? { value: selectedCategoryOption.value, label: selectedCategoryOption.label }
                          : null
                      }
                      loadOptions={loadCategoryOptions}
                      additional={{ page: 1 }}
                      onChange={(selected) => {
                        if (selected) {
                          formik.setFieldValue("category", selected.value, true);
                          setSelectedCategories([selected.value]);
                        } else {
                          formik.setFieldValue("category", "", true);
                          setSelectedCategories([]);
                        }
                      }}
                      onBlur={() => formik.setFieldTouched("category", true)}
                      isClearable
                      key={selectedBrand || "no-brand"}
                      className={formik.touched?.category && formik.errors?.category ? "is-invalid" : ""}
                      styles={{
                        control: (base: any) => ({
                          ...base,
                          minHeight: 38,
                          borderRadius: 8,
                          borderColor: formik.touched?.category && formik.errors?.category ? "#dc3545" : base.borderColor,
                        }),
                      }}
                    />
                    {formik.touched?.category && formik.errors?.category && (
                      <div className="invalid-feedback d-block">{formik.errors.category}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Product Description</label>
                    <textarea
                      id="description"
                      name="description"
                      className="form-control product-description-textarea"
                      rows={4}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{ borderRadius: 10, background: INPUT_BG, borderColor: BORDER, fontSize: 14 }}
                      placeholder="Enter product description..."
                    />
                  </div>

                  <div className="p-3 mb-3">
                    <div className="row g-2">
                      <div className="col-4">
                        <UIInputFF formik={formik} name="threshold_qty" label="Threshold Quantity" type="number" placeholder="0"  hideZero/>
                      </div>
                      <div className="col-4">
                        <UIReactSelect
                          formik={formik}
                          name="is_production"
                          placeholder="Select"
                          label=" Is Production"
                          required
                          options={[
                            // { value: "", label: "- Select -" },
                            { value: "yes", label: "Yes" },
                            { value: "no", label: "No" },
                          ]}
                        />
                      </div>
                      <div className="col-4">
                        <UIReactSelect
                          formik={formik}
                          name="statuss"
                          label="Status"
                          options={[
                            { value: "Active", label: "Active" },
                            { value: "Inactive", label: "Inactive" },
                          ]}
                          placeholder="Select"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT */}
                <div className="col-lg-6">
                  <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                    <i className="bi bi-currency-dollar pe-1" />
                    SKU & Pricing
                  </div>

                  <div className="mb-3">
                    <UIInputFF formik={formik} name="sku" label="SKU" required placeholder="Enter SKU" />
                  </div>

                  <div className="mb-3">
                    <UIInputFF formik={formik} name="purchase_price" label="Purchase Price" placeholder="0.00" type="number" hideZero />
                  </div>

                  <div className="mb-4">
                    <UIInputFF formik={formik} name="retail_price" label="Retail Price" required placeholder="0.00" type="number" hideZero />
                  </div>

                  <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                    <i className="bi bi-paperclip pe-1" />
                    Physical Properties
                  </div>

                  {/* Shipping & Dimensions */}
                  <div className="border rounded-4 p-3 mb-3">
                    <div className="fw-semibold mb-2">Shipping &amp; Dimensions</div>

                    <div className="mb-2 small">Shipping Configuration</div>

                    <div className="form-check mb-1">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="ship-global"
                        name="shipping_config"
                        value="global"
                        checked={formik.values.shipping_config === "global"}
                        onChange={() => {
                          formik.setFieldValue("shipping_config", "global");
                          // zero parent dims (shown in UI)
                          resetGlobalDimsToZero();
                          // optional: also zero any per-variant dims you may have edited earlier
                          resetAllCombinationDimsToZero();
                        }}
                      />
                      <label className="form-check-label" htmlFor="ship-global">
                        Same shipping for all variants
                      </label>
                    </div>

                    <div className="form-check mb-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="ship-per-variant"
                        name="shipping_config"
                        value="per_variant"
                        checked={formik.values.shipping_config === "per_variant"}
                        onChange={() => {
                          formik.setFieldValue("shipping_config", "per_variant");
                          // clear the parent dims (they won’t be used now)
                          resetGlobalDimsToZero();
                          // and zero per-variant dims so user starts fresh in Combinations edit
                          resetAllCombinationDimsToZero();
                        }}
                      />
                      <label className="form-check-label" htmlFor="ship-per-variant">
                        Different shipping per variant
                      </label>
                    </div>


                    {formik.values.shipping_config === "global" ? (
                      <>
                        <div className="mb-2 small text-muted">Dimensions/Measurements (Optional)</div>
                        <div className="row g-2">
                          <div className="col-4">
                            <UIInputFF formik={formik} name="length" type="number" label="Length(in)" placeholder="0.0" hideZero />
                          </div>
                          <div className="col-4">
                            <UIInputFF formik={formik} name="width" type="number" label="Width(in)" placeholder="0.0" hideZero />
                          </div>
                          <div className="col-4">
                            <UIInputFF formik={formik} name="height" type="number" label="Height(in)" placeholder="0.0" hideZero />
                          </div>
                          <div className="col-6">
                            <UIInputFF formik={formik} name="weight" type="number" label="Weight(lb)" placeholder="0.0" hideZero />
                          </div>
                        </div>
                      </>

                    ) : (
                      <div className="text-muted">
                        Dimensions will be configured individually for each variant in the Combinations tab.
                      </div>
                    )}
                  </div>


                  <div className="mb-3">
                    <UIReactSelect
                      formik={formik}
                      name="item_condition"
                      label="Item Condition"
                      placeholder="Brand New"
                      options={[
                        { value: "new", label: "New" },
                        { value: "used", label: "Used" },
                        { value: "damaged", label: "Damaged" },
                      ]}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">Condition Notes</label>
                    <textarea
                      id="condition_note"
                      name="condition_note"
                      className="form-control"
                      rows={4}
                      value={formik.values.condition_note}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{ borderRadius: 10, background: INPUT_BG, borderColor: BORDER, fontSize: 14 }}
                      placeholder="Enter condition notes..."
                    />
                  </div>
                  <div className="font-medium header-color-product  tracking-wide uppercase mb-4">
                    <div {...iconLabelProps(16)}>
                      <Settings  {...withIconStyle("#4a5565", 19)} />

                      <span className="header-color-product"> Tax Settings</span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <input
                      type="checkbox"
                      name="is_taxable"
                      id="is_taxable"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      checked={formik.values.is_taxable}
                      className="ms-2"
                    />
                    <label className="form-label ms-2">Is Taxable</label>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Tax Rule</label>
                    <select
                      className="form-control"
                      id="tax_rule_id"
                      name="tax_rule_id"
                      onChange={(e) => {
                        const value = e.target.value;
                        formik.setFieldValue("tax_rule_id", value);
                        if (value.startsWith("rule-")) {
                          const ruleId = value.replace("rule-", "");
                          const selectedRule = taxRules?.find((r: any) => r._id === ruleId);
                          if (selectedRule) formik.setFieldValue("taxRule", selectedRule);
                        } else if (value.startsWith("group-")) {
                          const groupId = value.replace("group-", "");
                          const selectedGroup = taxGroups?.find((g: any) => g.id === groupId);
                          if (selectedGroup) formik.setFieldValue("taxGroup", selectedGroup);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.tax_rule_id}
                      disabled={!formik.values.is_taxable}
                    >
                      <option value="">- Select -</option>
                      {taxGroups?.map((group: any) => (
                        <option key={`group-${group.id}`} value={`group-${group.id}`} style={{ fontWeight: "bold" }}>
                          {group.name}
                        </option>
                      ))}
                      {taxRules?.map((rule: any) => (
                        <option key={`rule-${rule._id}`} value={`rule-${rule._id}`}>
                          {`${rule.country} - ${rule.taxRate}%`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="col-12 mb-2" id="row-overlap">
                <hr className="" />
              </div>

              {/* Actions */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => safeNavigate(-1)}>
                  <i className="bi bi-chevron-left me-2" />
                  Back
                </button>
                {!isProductDetailsFilled ? (
                  <button
                    type="button"
                    className="btn btn-success save-btn surface-btn fw-bold fs-16"
                    // onClick={handleSaveDraft}
                    // disabled={formSubmitting}
                    disabled
                  >
                    Next
                  </button>

                ) : combinations.length === 0 ? (
                  // 🔵 Show NEXT if product details filled & combinations empty
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton save-btn surface-btn fw-bold fs-16"
                    disabled={formSubmitting || (Boolean(productId) && nextLocked)}
                    onClick={async () => {
                      if (formSubmitting) return;

                      // Ensure product exists; use returned pid instead of stale state
                      let pid = productId;
                      if (!pid) {
                        pid = await handleSaveDraft(); // <-- now returns the new productId
                        if (!pid) return;
                      }

                      // Defer the tab switch to the next frame so DOM reflects unlocked tabs
                      requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                          goToTab("#images");
                        });
                      });

                      // Optional: after first successful Next, disable this button
                      setNextLocked(true);
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <div className="mt-3 mb-3 d-flex justify-content-end">
                    <input
                      type="hidden"
                      id="product_type"
                      name="product_type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.product_type}
                    />
                    <button className="btn btn-primary table-addbutton save-btn surface-btn fw-bold fs-16" type="submit">
                      Save
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="tab-pane fade" id="images" role="tabpanel">
            <div className="card shadow-border-2">
              {/* BODY */}
              <div className="card-body pb-0">
                <h5 className="mb-1">Product Images</h5>
                <div className="text-muted mb-3">
                  Upload images for your product. You can upload multiple images at once.
                </div>

                {/* Dropzone */}
                <div
                  className="image-drop mt-2"
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith("image/"));
                    if (files.length) onPickFiles(files);
                  }}
                >
                  <i className="bi bi-upload fs-1 mb-2 opacity-75" />
                  <div className="fw-semibold">Upload Images</div>
                  <div className="small text-muted">Drag and drop images here, or click to select files</div>
                  <button
                    type="button"
                    className="btn btn-primary table-addbutton mt-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    Choose Files
                  </button>
                </div>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    if (files.length) onPickFiles(files);
                    e.currentTarget.value = "";
                  }}
                />

                {/* Empty state */}
                {uploaded.length === 0 && previewFiles.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-image fs-1 d-block mb-2 opacity-50" />
                    <div className="fw-semibold">No images added yet</div>
                    <div className="small">Upload images to see them here</div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
                      <div className="fw-semibold">
                        Images ({uploaded.length + previewFiles.length})
                        {uploaded.length > 0 && <span className="text-success ms-2">• {uploaded.length} Uploaded</span>}
                        {previewFiles.length > 0 && <span className="text-warning ms-2">• {previewFiles.length} Pending</span>}
                      </div>
                    </div>

                    {/* Grid */}
                    <div className="row g-3 mb-3">
                      {/* ✅ Show already uploaded images */}
                      {uploaded.map((img) => {
                        const src = getImageSrc(img.id, imageUrlMap);
                        return (
                          <div key={img.id} className="col-6 col-md-4 col-lg-3">
                            <div className="img-card">
                              <button
                                type="button"
                                className="btn-close img-x"
                                aria-label="Remove"
                                onClick={() => {
                                  // Mark for deletion
                                  setPendingDeleteIds(prev => [...prev, img.id]);
                                  setUploaded(prev => prev.filter(u => u.id !== img.id));
                                  showInfo("Image will be deleted when you save the product.");
                                }}
                              />
                              {src ? (
                                <img className="img-thumb" src={src} alt={img.name} />
                              ) : (
                                <div className="d-flex align-items-center justify-content-center text-muted small" style={{ height: 160 }}>
                                  Loading…
                                </div>
                              )}
                              {/* ✅ Badge to show it's uploaded */}
                              <span className="img-badge uploaded-badge">Uploaded</span>
                            </div>
                            <div className="small text-center mt-1 text-muted text-truncate">{img.name}</div>
                          </div>
                        );
                      })}

                      {/* ✅ Show pending preview images */}
                      {previewFiles.map((item: { file: File; url: string }, index: number) => (
                        <div key={`preview-${index}`} className="col-6 col-md-4 col-lg-3">
                          <div className="img-card">
                            <button
                              type="button"
                              className="btn-close img-x"
                              aria-label="Remove"
                              onClick={() => {
                                try { URL.revokeObjectURL(item.url); } catch { }
                                setPreviewFiles((prev: any[]) => prev.filter((_, i) => i !== index));
                              }}
                            />
                            <img
                              className="img-thumb"
                              src={item.url}
                              alt={item.file.name}
                            />
                            {/* ✅ Badge to show it's pending */}
                            <span className="img-badge pending-badge">Pending Upload</span>
                          </div>
                          <div className="small text-center mt-1 text-muted text-truncate">{item.file.name}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* FOOTER — only show after at least one image */}
              {previewFiles.length > 0 && (
                <div className="card-footer bg-transparent">
                  <div className="d-flex justify-content-start align-items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-light d-inline-flex align-items-center gap-2 rounded-4"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <i className="bi bi-cloud-arrow-up" />
                      Upload More Images
                    </button>

                    <button
                      type="button"
                      className="btn btn-primary table-addbutton"
                      onClick={handlePrepareProductImages}
                      disabled={imagesPreparing}
                      title="Upload selected images and attach to this product"
                    >
                      {imagesPreparing ? "Saving…" : "Save Images"}
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="tab-pane fade" id="combinations" role="tabpanel">
            {!isEditMode && (
              <div className="mt-2">
                <div className="row g-4">
                  {/* LEFT: combinations list */}
                  <div className="col-lg-9">
                    <h5 className="mb-3">Combinations List</h5>

                    {/* Desktop table */}
                    <div className="rdt-desktop">
                      <div className="card shadow-border-2 box-shadow-datatables ">
                        <div className="p-3" style={{ maxHeight: 420, overflowY: "auto" }}>
                          <table className="table align-middle mb-0">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th style={{ width: "8%" }}>SNO</th>
                                <th>Combination</th>
                                <th style={{ width: 160 }}>Price</th>
                                <th style={{ width: 80 }}>Edit</th>
                                <th style={{ width: 90 }}>Remove</th>
                              </tr>
                            </thead>
                            <tbody>
                              {combinations.length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="text-center text-muted">No combinations added yet.</td>
                                </tr>
                              ) : (
                                combinations.map((combination: any, idx: number) => (
                                  <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    <td className="text-wrap">
                                      {Object.entries(combination.attributes).map(([k, v]) => `${k}: ${v}`).join(", ")}
                                      {/* {(() => {
                                        const byIds = Array.isArray(combination.imageIds) ? combination.imageIds : [];
                                        const byIdx = Array.isArray((combination as any).imageIndexes)
                                          ? (combination as any).imageIndexes
                                          : [];
                                        let selectedThumbs: Array<{ key: string | number; src: string | undefined; title?: string }> = [];
                                        if (uploaded && uploaded.length && byIds.length) {
                                          selectedThumbs = uploaded
                                            .filter((u: any) => byIds.includes(u.id))
                                            .map((u: any) => ({
                                              key: u.id,
                                              src: getImageSrc(u.id, imageUrlMap),
                                              title: u.name,
                                            }));
                                        } else if (previewFiles && previewFiles.length && byIdx.length) {
                                          selectedThumbs = byIdx
                                            .map((ix: number) => ({
                                              key: ix,
                                              src: previewFiles[ix]?.url,
                                              title: previewFiles[ix]?.file?.name,
                                            }))
                                        }

                                        const count = selectedThumbs.length;

                                        return (
                                          <>
                                            <div className="mini-gallery mt-2">
                                              {count > 0 ? (
                                                <div className="d-flex align-items-center gap-2">

                                                  {selectedThumbs?.map((t: any) => (
                                                    <span key={t.key} className="mini-thumb is-selected" title={t.title || ""}>
                                                      <img src={t.src!} alt="" />
                                                    </span>
                                                  ))}
                                                </div>
                                              ) : (
                                                <span className="small text-muted"></span>
                                              )}
                                            </div>

                                            <div className="small text-muted mt-1">
                                              {count > 0 ? `${count} image${count > 1 ? "s" : ""} selected` : "No images selected"}
                                            </div>
                                          </>
                                        );
                                      })()} */}
                                      {(() => {
                                        // ---- inputs ----
                                        const byIds: string[] = Array.isArray(combination.imageIds) ? combination.imageIds : [];
                                        const byLocalKeys: string[] = Array.isArray((combination as any).imageLocalKeys)
                                          ? (combination as any).imageLocalKeys
                                          : [];
                                        const byIdx: number[] = Array.isArray((combination as any).imageIndexes)
                                          ? (combination as any).imageIndexes
                                          : [];

                                        // Build a quick map for local previews:  "name|size" -> objectURL
                                        const previewMap = new Map<string, { url?: string; title?: string }>();
                                        (previewFiles || []).forEach(p => {
                                          const k = `${p?.file?.name || ""}|${p?.file?.size || 0}`;
                                          previewMap.set(k, { url: p?.url, title: p?.file?.name });
                                        });

                                        // 1) Local (not-yet-uploaded) selections come first
                                        const localThumbs =
                                          byLocalKeys
                                            .map(k => {
                                              const hit = previewMap.get(k);
                                              return hit?.url ? { key: `local:${k}`, src: hit.url, title: hit.title } : null;
                                            })
                                            .filter(Boolean) as Array<{ key: string; src: string; title?: string }>;

                                        // 2) Server image ids (already uploaded / existing)
                                        const serverThumbs =
                                          byIds
                                            .map(id => {
                                              const src = getImageSrc(id, imageUrlMap); // pass the correct 2nd arg for your helper
                                              return src ? { key: `id:${id}`, src, title: (uploaded || []).find(u => u.id === id)?.name } : null;
                                            })
                                            .filter(Boolean) as Array<{ key: string; src: string; title?: string }>;

                                        // 3) Legacy fallback: index-based selection into previewFiles (if you still have old rows)
                                        const idxThumbs =
                                          byIdx
                                            .map(ix => {
                                              const pf = previewFiles?.[ix];
                                              return pf?.url ? { key: `idx:${ix}`, src: pf.url, title: pf.file?.name } : null;
                                            })
                                            .filter(Boolean) as Array<{ key: string; src: string; title?: string }>;

                                        // Final order: local first (what user just picked), then server, then legacy index
                                        const selectedThumbs = [...localThumbs, ...serverThumbs, ...idxThumbs];
                                        const count = selectedThumbs.length;

                                        return (
                                          <>
                                            <div className="mini-gallery mt-2">
                                              {count > 0 ? (
                                                <div className="d-flex align-items-center gap-2">
                                                  {selectedThumbs.map(t => (
                                                    <span key={t.key} className="mini-thumb is-selected" title={t.title || ""}>
                                                      <img src={t.src} alt="" />
                                                    </span>
                                                  ))}
                                                </div>
                                              ) : (
                                                <span className="small text-muted"></span>
                                              )}
                                            </div>

                                            <div className="small text-muted mt-1">
                                              {count > 0 ? `${count} image${count > 1 ? "s" : ""} selected` : "No images selected"}
                                            </div>
                                          </>
                                        );
                                      })()}

                                    </td>



                                    <td>
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="0"
                                        value={combination.price == 0 ? "" : combination.price}
                                        onChange={(e) => {
                                          const price = parseFloat(e.target.value) || 0;
                                          setCombinations((prev: any[]) =>
                                            prev.map((c, i) => (i === idx ? { ...c, price } : c))
                                          );
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <EditIconButton
                                        type="button"
                                        className="btn btn-link p-0"
                                        onClick={() => { setIsEditMode(true); setEditIndex(idx); }}
                                        title="Edit"
                                      />
                                    </td>
                                    <td>
                                      {(() => {
                                        // ✅ Prefer real variant id; only treat as "saved" if it's 24-hex
                                        const variantId = String(combination?.variant_id ?? combination?.id ?? "");
                                        const hasServerId = is24Hex(variantId);
                                        // delKey drives spinner/disabled; use server id if present, else a tmp key per row
                                        const delKey = hasServerId ? variantId : `tmp-${idx}`;
                                        return (
                                          <DeleteIconButton
                                            type="button"
                                            className="btn btn-link text-danger p-0"
                                            title="Remove"
                                            disabled={deletingIds.has(delKey)}
                                            onClick={async () => {
                                              setDeletingIds(prev => new Set(prev).add(delKey));
                                              try {
                                                // ✅ Only call API if variant has a valid server ID
                                                if (hasServerId) {
                                                  await deleteVariantFromServer(variantId);   // ✅ real server delete
                                                }
                                                // remove from UI either way
                                                setCombinations(prev => prev.filter((_, i) => i !== idx));
                                                showSuccess?.("Variant deleted.");
                                              } catch (err: any) {
                                                safeConsole.error(err);
                                                showError?.(err?.message || "Failed to delete variant.");
                                              } finally {
                                                setDeletingIds(prev => {
                                                  const n = new Set(prev);
                                                  n.delete(delKey);
                                                  return n;
                                                });
                                              }
                                            }}
                                          >
                                            {deletingIds.has(delKey) ? (
                                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            ) : (
                                              <i className="bi bi-trash" />
                                            )}
                                          </DeleteIconButton>

                                        );
                                      })()}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                    <div className="rdt-mobile">
                      <MobileCardsOnly table={mobileTable} />
                    </div>
                  </div>

                  {/* RIGHT: attributes + Generate (like screenshot) */}
                  <div className="col-lg-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h5 className="mb-0">Attributes</h5>
                      <button type="button" className="btn btn-primary table-addbutton" onClick={handleGenerateAndCreate}>
                        Generate
                      </button>
                    </div>

                    <div className="card shadow-border-2 box-shadow-datatables p-3">
                      <div style={{ maxHeight: 520, overflowY: "auto" }}>
                        {(Array.isArray(attributes) ? attributes : [])?.map((attr: any, index: number) => (
                          <div key={index} className="mb-4">
                            <div className="fw-bold text-secondary mb-2">{attr.name}</div>
                            <ul className="list-group">
                              {attr.values?.map((param: string, pIdx: number) => {
                                const selected = options.some((opt: any) => opt[attr.name.toLowerCase()] === param);
                                return (
                                  <li
                                    key={pIdx}
                                    className={`list-group-item list-group-item-action ${selected ? "active" : ""}`}
                                    onClick={() => handleAttributeClick(attr.name, param)}
                                    style={{
                                      cursor: "pointer",
                                      backgroundColor: selected ? "#dcfce7" : "#fff",
                                      color: selected ? "#016630" : "#364153",
                                      border: selected ? "1px solid #7bf1a8" : "1px solid #364153",
                                      transition: "all .2s",
                                    }}
                                  >
                                    {param}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ====== EDIT MODE (matches screenshot with sticky save) ====== */}
            {isEditMode && editIndex !== null && (
              <div className="mt-2">
                <div className="d-flex align-items-center mb-3">
                  <button type="button" className="btn btn-light fw-bold" onClick={() => setIsEditMode(false)}>
                    <i className="bi bi-chevron-left me-2" />
                    Back to Combinations
                  </button>
                </div>

                <div className="card shadow-border-2 box-shadow-datatables p-3">
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-md-5">
                        <UIInputFF
                          formik={{
                            values: {
                              tmpName: Object.entries(combinations[editIndex].attributes)
                                .map(([k, v]: any) => `${k}: ${v}`).join(", ")
                            },
                            handleChange: () => { },
                            handleBlur: () => { },
                          } as any}
                          name="tmpName"
                          label="Product Name"
                          required
                          placeholder=""
                        />
                      </div>
                      <div className="col-md-3">
                        <UIInputFF
                          formik={{
                            values: { tmpSku: combinations[editIndex]?.sku || "" },
                            errors: {},
                            touched: {},
                            handleChange: (e: any) =>
                              setCombinations((prev: any[]) =>
                                prev.map((c, i) => (i === editIndex ? { ...c, sku: e.target.value.trim() } : c))
                              ),
                            handleBlur: () => { },
                            setFieldValue: (_field: string, value: any) => {
                              setCombinations((prev: any[]) =>
                                prev.map((c, i) => (i === editIndex ? { ...c, sku: String(value || "").trim() } : c))
                              );
                            },
                            setFieldTouched: () => { },
                          } as any}
                          name="tmpSku"
                          label="SKU"
                          placeholder="SKU"
                        />
                      </div>
                      <div className="col-md-4">
                        <UIInputFF
                          formik={{
                            values: { tmpPrice: combinations[editIndex]?.price ?? 0 },
                            errors: {},
                            touched: {},
                            handleBlur: () => { },
                            handleChange: (e: any) => {
                              const v = parseFloat(e?.target?.value ?? e) || 0;
                              setCombinations(prev => prev.map((c, i) => (i === editIndex ? { ...c, price: v } : c)));
                            },
                            setFieldValue: (_field: string, value: any) => {
                              const v = parseFloat(value) || 0;
                              setCombinations(prev => prev.map((c, i) => (i === editIndex ? { ...c, price: v } : c)));
                            },
                            setFieldTouched: () => { },
                          } as any}
                          name="tmpPrice"
                          label="Price"
                          placeholder="0"
                          type="number"
                          hideZero
                        />
                      </div>
                      {isEditMode && editIndex !== null && (
                        <div className="combo-picker rounded-3 p-3 mb-3">
                          <div className="fw-semibold mb-2">Select Images for this Combination</div>
                          <div className="small text-muted mb-2">
                            Choose one or more images to associate with this product combination
                          </div>

                          {uploaded.length === 0 && previewFiles.length === 0 ? (
                            <div className="text-muted text-center py-3">
                              No images available. Upload images in the Images tab first.
                            </div>
                          ) : (
                            <div className="d-flex flex-wrap gap-3">
                              {/* ✅ Server images (already uploaded) */}
                              {uploaded.map((u) => {
                                const src = getImageSrc(u.id, imageUrlMap);
                                const currentIds = combinations[editIndex!]?.imageIds ?? [];
                                const selected = currentIds.includes(u.id);

                                return (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => {
                                      setCombinations(prev => prev.map((c, idx) => {
                                        if (idx !== editIndex) return c;
                                        const set = new Set(c.imageIds ?? []);
                                        selected ? set.delete(u.id) : set.add(u.id);
                                        return { ...c, imageIds: Array.from(set) };
                                      }));
                                    }}
                                    className={`combo-thumb ${selected ? "is-selected" : ""}`}
                                    title={selected ? "Unselect image" : "Select image"}
                                  >
                                    <div className="combo-thumb-inner">
                                      {src ? (
                                        <img src={src} alt={u.name} />
                                      ) : (
                                        <div className="d-flex align-items-center justify-content-center text-muted small" style={{ height: 100 }}>
                                          Loading…
                                        </div>
                                      )}
                                    </div>
                                    {selected && <span className="combo-check">✓</span>}
                                  </button>
                                );
                              })}

                              {/* ✅ Local previews (not yet uploaded - can't be selected) */}
                              {previewFiles.map((p, i) => {
                                const localKey = `${p.file.name}|${p.file.size}`;
                                const currentLocal = combinations[editIndex!]?.imageLocalKeys ?? [];
                                const selected = currentLocal.includes(localKey);

                                return (
                                  <div
                                    key={localKey}
                                    className="combo-thumb opacity-50"
                                    title="Click 'Save Images' to upload this image first"
                                  >
                                    <div className="combo-thumb-inner">
                                      <img src={p.url} alt={p.file.name} />
                                    </div>
                                    <span className="combo-pending-label">Not Uploaded</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="small mt-2 text-primary">
                            {(combinations[editIndex].imageIds || []).length} image
                            {(combinations[editIndex].imageIds || []).length !== 1 ? "s" : ""} selected
                          </div>
                          {previewFiles.length > 0 && (
                            <div className="small mt-1 text-warning">
                              ⚠️ {previewFiles.length} image{previewFiles.length !== 1 ? "s" : ""} not uploaded yet. Click "Save Images" in the Images tab.
                            </div>
                          )}
                        </div>
                      )}



                      {formik.values.shipping_config === "per_variant" && editIndex !== null && (
                        <div className="dim-card p-3 my-2">
                          <div className="dim-title">Dimensions &amp; Weight</div>

                          <div className="row g-3">
                            <div className="col-md-3">
                              <label className="form-label dim-label">Length (in)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].length ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, length: v } : c))
                                  );
                                }}
                              />
                            </div>

                            <div className="col-md-3">
                              <label className="form-label dim-label">Width (in)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].width ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, width: v } : c))
                                  );
                                }}
                              />
                            </div>

                            <div className="col-md-3">
                              <label className="form-label dim-label">Height (in)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].height ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, height: v } : c))
                                  );
                                }}
                              />
                            </div>

                            <div className="col-md-3">
                              <label className="form-label dim-label">Weight (lb)</label>
                              <input
                                type="number"
                                className="form-control dim-input"
                                placeholder="0.0"
                                value={combinations[editIndex].weight ?? 0}
                                onChange={(e) => {
                                  const v = Number(e.target.value) || 0;
                                  setCombinations(prev =>
                                    prev.map((c, i) => (i === editIndex ? { ...c, weight: v } : c))
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}



                      <div className="col-12">
                        <label className="form-label">Description</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={combinations[editIndex].description || ""}
                          onChange={(e) =>
                            setCombinations((prev: any[]) =>
                              prev.map((c, i) => (i === editIndex ? { ...c, description: e.target.value } : c))
                            )
                          }
                        />
                      </div>
                    </div>

                    <hr className="my-4" />
                    <h6 className="mb-2">Stock Details:</h6>

                    {/* header */}
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <colgroup>
                          <col style={{ width: "12%" }} />  {/* Warehouse */}
                          <col style={{ width: "7%" }} />  {/* Available */}
                          <col style={{ width: "7%" }} />  {/* Zone */}
                          <col style={{ width: "7%" }} />  {/* Rack */}
                          <col style={{ width: "7%" }} />  {/* Bin Location */}
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col">Warehouse</th>
                            <th scope="col">Available</th>
                            <th scope="col">Zone</th>
                            <th scope="col">Rack</th>
                            <th scope="col">Bin Location</th>
                          </tr>
                        </thead>

                      </table>
                    </div>
                    <div style={{ maxHeight: 420, overflowY: "auto" }}>
                      {combinations[editIndex].warehouseData.map((row: any, wi: number) => (
                        <div key={`${editIndex}-${row.warehouse_id}`} className="rounded-4 border p-3 mt-2 bg-white shadow-sm">
                          <div className="row g-3">
                            <div className="col-xl-3 col-md-6">
                              <input className="form-control" readOnly value={row.warehouse_name} />
                            </div>

                            <div className="col-xl-2 col-md-6">
                              <input
                                type="number"
                                min={0}
                                className="form-control"
                                value={row.available === 0 ? "" : row.available}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setCombinations((prev: any[]) =>
                                    prev.map((c, i) =>
                                      i === editIndex
                                        ? { ...c, warehouseData: c.warehouseData.map((w: any, j: number) => (j === wi ? { ...w, available: val, on_hand: val } : w)) }
                                        : c
                                    )
                                  );
                                  setCombErrors((prev: any[][]) => {
                                    const copy = prev.map((arr) => arr.slice());
                                    copy[editIndex][wi] = { zone: false, rack: false, bin: false };
                                    return copy;
                                  });
                                }}
                              />
                            </div>

                            <div className="col-xl-2 col-md-6">
                              <select
                                disabled={!(row.available > 0)}
                                className={`form-control ${combErrors[editIndex][wi].zone ? "is-invalid" : ""}`}
                                value={row.zone_id}
                                onChange={(e) => {
                                  const opt = e.target.selectedOptions[0];
                                  handleZoneChange(editIndex, wi, opt.value, row.warehouse_id, opt.text);
                                  setCombErrors((prev: any[][]) => {
                                    const copy = prev.map((arr) => arr.slice());
                                    copy[editIndex][wi].zone = false;
                                    return copy;
                                  });
                                }}
                              >
                                <option value="">Select Zone</option>
                                {locationOptions[row.warehouse_id]?.zones.map((z: any) => (
                                  <option key={z._id} value={z._id}>{z.label}</option>
                                ))}
                              </select>
                            </div>

                            <div className="col-xl-2 col-md-6">
                              <select
                                disabled={!(row.available > 0) || !row.zone_id}
                                className={`form-control ${combErrors[editIndex][wi].rack ? "is-invalid" : ""}`}
                                value={row.rack_id}
                                onChange={(e) => {
                                  const opt = e.target.selectedOptions[0];
                                  handleRackChange(editIndex, wi, opt.value, row.warehouse_id, opt.text);
                                  setCombErrors((prev: any[][]) => {
                                    const copy = prev.map((arr) => arr.slice());
                                    copy[editIndex][wi].rack = false;
                                    return copy;
                                  });
                                }}
                              >
                                <option value="">Select Zone</option>
                                {(locationOptions[row.warehouse_id]?.racks || [])
                                  .filter((r: any) => r.zone_id === row.zone_id)
                                  .map((r: any) => (
                                    <option key={r._id} value={r._id}>{r.label}</option>
                                  ))}
                              </select>
                            </div>

                            <div className="col-xl-3 col-md-6">
                              <select
                                disabled={!(row.available > 0) || !row.rack_id}
                                className={`form-control ${combErrors[editIndex][wi].bin ? "is-invalid" : ""}`}
                                value={row.bin_id}
                                onChange={(e) => {
                                  const opt = e.target.selectedOptions[0];
                                  setCombinations((prev: any[]) =>
                                    prev.map((c, i) =>
                                      i === editIndex
                                        ? { ...c, warehouseData: c.warehouseData.map((w: any, j: number) => (j === wi ? { ...w, bin_id: opt.value, bin_name: opt.text } : w)) }
                                        : c
                                    )
                                  );
                                  setCombErrors((prev: any[][]) => {
                                    const copy = prev.map((arr) => arr.slice());
                                    copy[editIndex][wi].bin = false;
                                    return copy;
                                  });
                                }}
                              >
                                <option value="">Select Rack</option>
                                {locationOptions[row.warehouse_id]?.bins.map((b: any) => (
                                  <option key={b._id} value={b._id}>{b.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* sticky bottom action bar */}
                  <div className="position-sticky bottom-0 bg-white border-top d-flex justify-content-end p-3">
                    <button
                      type="button"
                      className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                      onClick={async () => {
                        // Validate first
                        const idx = editIndex;
                        const combo = Array.isArray(combinations) ? combinations[idx] : undefined;
                        const rows = combo?.warehouseData ?? [];

                        if (!Array.isArray(rows) || rows.length === 0) {
                          showInfo("No warehouse rows to save.");
                          return;
                        }

                        // Build an error matrix for this edit row
                        const nextErrors = rows.map(() => ({ zone: false, rack: false, bin: false }));

                        // Require at least one qty > 0
                        const hasAnyQty = rows.some((r: any) => Number(r?.available) > 0);
                        if (!hasAnyQty) {
                          showInfo("Enter quantity for at least one warehouse.");
                          setCombErrors((prev: any) => {
                            const copy = Array.isArray(prev) ? [...prev] : [];
                            copy[idx] = nextErrors;
                            return copy;
                          });
                          return;
                        }

                        // For any row with qty > 0, require zone/rack/bin
                        let hasError = false;
                        rows.forEach((row: any, wi: number) => {
                          if (Number(row?.available) > 0) {
                            if (!row?.zone_id) { nextErrors[wi].zone = true; hasError = true; }
                            if (!row?.rack_id) { nextErrors[wi].rack = true; hasError = true; }
                            if (!row?.bin_id) { nextErrors[wi].bin = true; hasError = true; }
                          }
                        });

                        if (hasError) {
                          showInfo("Please select a zone, rack and bin for your selected warehouse.");
                          setCombErrors((prev: any) => {
                            const copy = Array.isArray(prev) ? [...prev] : [];
                            copy[idx] = nextErrors;
                            return copy;
                          });
                          return;
                        }

                        // All good → save this combination (PATCH)
                        try {
                          await handleSaveEditedCombination(); // this should close edit mode & toast success
                        } catch (e) {
                          // handleSaveEditedCombination already handles errors; optional log
                          safeConsole.error(e);
                        }
                      }}
                    >
                      Save Combination
                    </button>

                  </div>
                </div>
              </div>
            )}
          </div>
          {/* <div className="tab-pane fade" id="volume-discount" role="tabpanel"> */}
          {/* <SignageVolumeDiscount
              productType={formik.values.product_type === "variable" ? "variable" : "simple"}
              combinations={variantCombos}      // ensure combinations[i].id is your variant_id
              value={volumeDiscounts}
              onChange={setVolumeDiscounts}
              productId={productId}             // ✅
              token={token}                     // ✅
            /> */}
          {/* </div> */}


          {/* <div className="tab-pane fade" id="volume-discount" role="tabpanel">
    <SignageVolumeDiscount
      productType={formik.values.product_type === "variable" ? "variable" : "simple"}
      combinations={variantCombos}
      value={volumeDiscounts}
      onChange={setVolumeDiscounts}
    />
  </div> */}

          <div className="tab-pane fade" id="volume-discount" role="tabpanel">
            {canShowVolumeDiscount ? (
              <SignageVolumeDiscount
                productType={formik.values.product_type === "variable" ? "variable" : "simple"}
                combinations={combosForDiscount}
                value={volumeDiscounts}
                onChange={setVolumeDiscounts}
                productId={productId}
                token={token}
              />
            ) : (
              <div className="alert alert-info">
                Generate the ComBinations, then add to manage volume discounts.
              </div>
            )}
          </div>



          {/* ================= CUSTOMIZATION ================= */}
          <div className="tab-pane fade" id="customization" role="tabpanel">
            <div className="card shadow-border-2 box-shadow-datatables p-3">
              <div className="card-body">
                <CustomFieldBuilder onSave={(fields) => setCustomFields(fields)} initialFields={customFields} />
              </div>
            </div>
          </div>

          {/* ================= DESIGN ================= */}
          <div className="tab-pane fade" id="design" role="tabpanel">
            {productId && variables.length > 0 ? (
              <DesignsTab
                productType="variable"
                mode="add"
                combinations={variables}
                productId={productId}
              />
            ) : (
              <div className="alert alert-info">
                {!productId
                  ? "Save the product first, then generate variants to manage design assignments."
                  : "Generate variants first, then open this tab to manage design assignments."}
              </div>
            )}
          </div>
          {/* <div className="tab-pane fade" id="volume-discount" role="tabpanel">
            {productId ? (
              <SignageVolumeDiscount
                productType={formik.values.product_type === "variable" ? "variable" : "simple"}
                combinations={combinationsfetch}
                value={volumeDiscounts}
                onChange={setVolumeDiscounts}
                productId={productId}   // now guaranteed string
                token={token}
              />
            ) : (
              <div className="alert alert-info">
                Save the product first, then open this page in Edit mode to manage volume discounts.
              </div>
            )}
          </div> */}
        </div>
      </form >

      {/* styles (tabs color + responsiveness) */}
      < style > {`
      .activetabs.locked { opacity: 0.6; cursor: not-allowed; }
.rc-tooltip .tooltip-inner { font-weight: 600; }

        .rc-tabs .activetabs{
          border:1px solid #e5e7eb;background:#f8f9fb;padding:10px 16px;border-radius:12px;
          font-weight:600;color:#374151;box-shadow:0 2px 10px rgba(0,0,0,.03)
        }
        .rc-tabs .activetabs:hover{background:#eef2ff}
        .rc-tabs .activetabs.disabled{pointer-events:none;opacity:.5}
  
        /* different active bg per tab */
        .rc-tabs a.activetabs.active[href="#product-details"]{background:#2563eb;color:#fff;border-color:#2563eb;box-shadow:0 6px 18px rgba(37,99,235,.18)}
        .rc-tabs a.activetabs.active[href="#images"]{background:#7c3aed;color:#fff;border-color:#7c3aed;box-shadow:0 6px 18px rgba(124,58,237,.18)}
        .rc-tabs a.activetabs.active[href="#combinations"]{background:#16a34a;color:#fff;border-color:#16a34a;box-shadow:0 6px 18px rgba(22,163,74,.18)}
        .rc-tabs a.activetabs.active[href="#volume-discount"]{background:#2563eb;color:#fff;border-color:#2563eb;box-shadow:0 6px 18px rgba(37,99,235,.18)}
        .rc-tabs a.activetabs.active[href="#customization"]{background:#f97316;color:#fff;border-color:#f97316;box-shadow:0 6px 18px rgba(249,115,22,.18)}
        .rc-tabs a.activetabs.active[href="#design"]{background:#ec4899;color:#fff;border-color:#ec4899;box-shadow:0 6px 18px rgba(236,72,153,.18)}
  
        /* desktop vs mobile */
        .rdt-desktop{display:block}
        .rdt-mobile{display:none}
        @media (max-width:1021px){.rdt-desktop{display:none}.rdt-mobile{display:block}}

        /* Image upload and preview styles */
        .imageuploadui{border:2px dashed #d1d5db;border-radius:14px;padding:40px 12px;text-align:center;cursor:pointer;transition:all .2s}
        .imageuploadui:hover{border-color:#6d28d9;background:#fbfaff}
        .uploadedimages{border-radius:10px}
          /* Thumbnail card so all X icons share the same anchor */
.img-card{
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
  height: 180px;                 /* same height for all tiles */
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Dropzone */
.image-drop {
  min-height: 180px;
  border: 2px dashed #d7dde5;
  border-radius: 12px;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  cursor: pointer;
  transition: border-color .15s ease, background .15s ease;
}
.image-drop:hover { border-color: #9ca3af; background: #fafcff; }

/* Thumbs */
.img-thumb {
  width: 100%;
  height: 160px;
  object-fit: contain;
  border-radius: 12px;
  background: #fff;
  border: 1px solid #e5e7eb;
  display: block;
}

/* Close (X) */
.img-x{
  position: absolute;
  top: 0px;
  right: 0px;
  z-index: 2;
  opacity: 0.9;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
  padding: 0;
}

/* --- Dimensions card --- */
.dim-card {
  background: #f6f7fb;               /* soft panel bg */
  border: 1px solid rgba(2,6,23,.06); /* subtle border */
  border-radius: 14px;
}

/* title inside the panel */
.dim-card .dim-title {
  font-weight: 600;
  margin-bottom: 10px;
}

/* --- Inputs look like screenshot --- */
.form-control.dim-input {
  background-color: #f1f5f9 !important; /* light input bg */
  border: 1px solid #e5e7eb !important; /* soft border */
  border-radius: 12px !important;
  padding: 10px 12px;
  box-shadow: none !important;
}

.form-control.dim-input::placeholder {
  color: #9ca3af; /* muted placeholder (0.0) */
}

/* Focus ring (subtle blue) */
.form-control.dim-input:focus {
  border-color: #bfdbfe !important;
  box-shadow: 0 0 0 3px #dbeafe !important; /* light ring */
  background-color: #f8fafc !important;
}

/* Labels spacing like screenshot */
.dim-label {
  font-weight: 600;
  margin-bottom: 6px;
  color: #1f2937;
}

/* ===== Combination Image Picker (edit panel) ===== */
.combo-picker {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.combo-thumb {
  position: relative;
  width: 150px;
  padding: 0;
  border: 0;
  background: transparent;
}

.combo-thumb-inner {
  border: 1.5px solid #e5e7eb;
  border-radius: 12px;
  padding: 6px;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease, transform .05s ease;
}

.combo-thumb-inner:hover {
  transform: translateY(-1px);
}

.combo-thumb img {
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 8px;
}

.combo-thumb.is-selected .combo-thumb-inner {
  border-color: #3b82f6; /* blue */
  box-shadow: 0 0 0 3px rgba(59,130,246,.15);
}

/* top-right check badge like your screenshot */
.combo-check {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
  background: #3b82f6;
  color: #fff;
  border: 2px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

/* ===== Mini gallery inside combinations table cell ===== */
.mini-gallery {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-default-badge {
  width: 18px; height: 18px;
  border-radius: 9999px;
  background: #3b82f6;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}

.mini-thumb {
  width: 28px; height: 28px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.mini-thumb.is-selected {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59,130,246,.15);
}

.mini-thumb img {
  width: 100%; height: 100%; object-fit: contain;
}


/* optional: hover feedback */
.img-card:hover .img-x{ opacity: 1; }

/* ===== Image status badges ===== */
.img-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  z-index: 2;
}

.uploaded-badge {
  background: #dcfce7;
  color: #166534;
  border: 1px solid #86efac;
}

.pending-badge {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
}

/* ===== Pending upload label for combo picker ===== */
.combo-pending-label {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  background: rgba(251, 191, 36, 0.95);
  color: #78350f;
  z-index: 3;
  white-space: nowrap;
}
      `}</style >
    </div >
  );


};

export default SignageAddVariableProduct;










